/**
 * Lift2Fit — the session runtime: "the watch".
 *
 * One state machine, host-agnostic. The app hosts it over the Grid; the landing
 * page hosts the same component over the throwaway demo grid; phone and watchOS
 * are designed for, not built here.
 *
 *   ready → countdown → working → resting → … → capture → complete
 *
 * Everything is derived from the movement's real prescription — weight, sets,
 * rest, grouped work and tempo are never guessed.
 *
 * TIME MODEL — two clocks, never conflated
 *  - The SESSION timer is the continuous elapsed-since-Start-Session clock. It
 *    sits in the top strip on the RIGHT, beside the wall-clock time, and runs
 *    whether the transport is playing or paused.
 *  - The SET timer belongs to the current set and its rest. It is the centre
 *    cell of the ONE timer strip: the wall clock on its left, the continuous
 *    SESSION timer on its right, all three on one baseline. The 10 → 1 countdown
 *    and the rest clock replace it IN PLACE — same cell, same fixed dimensions,
 *    never a second panel. The countdown and the set clock never show at once.
 *  - The transport is the primary action: press = play/pause, press and hold
 *    (~5 s, with a cancelling progress ring) = Done set. The play/pause symbol is
 *    the button's whole content (the timer left it), so the symbol shows what a
 *    press will do. Enter and Space are play/pause; holding Enter is Done set.
 *  - REST is owned by the set it follows: the rest that follows a set is stored
 *    per step (movement:set) the moment that step is seeded, so a REST edit is
 *    remembered for that set and navigating with ‹/› cannot reset it. Done set
 *    starts the rest from THAT stored value, and while the rest runs the REST
 *    field shows the value in force — editing it retargets the running clock.
 *  - Pause freezes the displayed clock at its value; play resumes from there.
 *    The RECORDED exercise and rest durations are real wall-clock times and
 *    accrue regardless of play/pause (the paused-display time is what freezes,
 *    never the record). There are no server columns for them, so they live
 *    client-side per session (localStorage `l2f.runtime.times.<sessionId>`) and
 *    are shown honestly in the summary.
 *  - Done set ends the set and starts the rest; it does NOT advance. The screen
 *    keeps showing the just-completed set, still editable, through the rest so
 *    the lifter can correct weight/reps/RPE — an edit PATCHes that set. The
 *    advance to the next set (or the next movement) happens at the END of the
 *    rest countdown.
 *
 * The working screen is a media player, not a form. A VERTICAL three-field
 * **drum** shows the active field in the middle with the previous field above and
 * the next below — dimmed, angled and never drawn as outlined buttons. Rotating
 * (a vertical swipe, ↑/↓ or Home/End, or tapping a neighbour) rolls a new field
 * into the middle; the explicit field chips bring their field into the middle
 * too. The middle field's value is changed only by the ONE +/− pair, and those
 * two keys live in the transport row — `‹  −  ▶/⏸  +  ›` — with the play/pause
 * centred and emphasised. `‹`/`›` own previous/next SET, so they are context
 * aware rather than duplicated by extra buttons: `›` starts the set during the
 * countdown, skips the rest, and moves to the next set.
 *
 * A set written here is written exactly as the Grid writes it: same endpoint,
 * same `clientSetId` idempotency, through the host's callbacks which reuse the
 * `lib/api` client functions — and only with the fields valid for the active
 * measure, so a `0` distance or an empty tempo can never 400 a save.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UnitSystem } from '@lift2fit/shared-types';
import {
  flushPendingWrites,
  pendingWriteStatus,
  subscribePendingWrites,
  type ActiveSessionPayload,
  type SessionExercise,
  type SessionSet,
  type SessionTotalsWire,
  type SetMeasure,
  type SetStatus,
} from '@/lib/api';
import { cn, pluralize } from '@/lib/format';
import { MOOD_LABELS, formatPrescription } from '@/lib/prescription';
import { displayToKg, formatVolume, formatWeight, kgToDisplay } from '@/lib/units';
import {
  AlertIcon,
  CheckIcon,
  ChevronDownIcon,
  CloseIcon,
  MinusIcon,
  PlusIcon,
  TimerIcon,
  TrashIcon,
} from '@/lib/icons';
import { CollapsedNote, groupTagText } from './components';
import { parsePrescription, prescriptionLine, type ExercisePrescription, type SetDraft } from './grid';
import { isRuntimeMuted, playRuntimeTone, setRuntimeMuted, unlockRuntimeAudio, type RuntimeTone } from './runtime-sounds';

/* ---------------------------------------------------------------------------
   The model the panel runs on — no API types past this line, so every host
   (app, landing demo, later phone/watch) supplies the same shape.
   ------------------------------------------------------------------------ */

export interface RuntimeSetState {
  /** null until a host has persisted the set (the landing demo never persists). */
  id: string | null;
  setNumber: number;
  weightKg: number;
  reps: number;
  rpe: number | null;
  measure: SetMeasure;
  distanceM: number | null;
  durationS: number | null;
  status: SetStatus;
  isPr: boolean;
}

export interface RuntimeMovement {
  id: string;
  exerciseId: string;
  name: string;
  prescription: ExercisePrescription;
  notes: string | null;
  /**
   * True when the movement is part of the block's shared warm-up (0019).
   * Optional so a host fixture written before warm-ups still type-checks; the
   * API always sends it and `runtimeModelFromPayload` always fills it.
   */
  isWarmup?: boolean;
  /** last time's working sets, formatted — empty on a first run. */
  previous: string[];
  sets: RuntimeSetState[];
  /** the api shape for a host that has one; the panel never reads it */
  source?: SessionExercise;
}

export interface RuntimeTotals {
  volumeKg: number;
  totalReps: number;
  setsCompleted: number;
  setsPrescribed: number | null;
  prCount: number;
  durationMin: number;
}

export interface RuntimeModel {
  sessionId: string;
  label: string | null;
  startedAt: string;
  movements: RuntimeMovement[];
  totals: RuntimeTotals | null;
}

export interface RuntimeWriteResult {
  set: RuntimeSetState;
  totals: RuntimeTotals | null;
  isPr: boolean;
}

/**
 * A write either succeeded (with the persisted set) or failed with the API's own
 * reason. A host must never swallow the problem: the panel prints `detail` so a
 * future failure is diagnosable in one look.
 */
export type RuntimeWriteOutcome = RuntimeWriteResult | { ok: false; detail: string };

function isWriteFailure(value: RuntimeWriteOutcome): value is { ok: false; detail: string } {
  return 'ok' in value && value.ok === false;
}

export interface RuntimeScore {
  perceivedExertion: number | null;
  moodScore: number | null;
  painScore: number | null;
}

export interface RuntimeCompletion {
  totals: RuntimeTotals | null;
}

/**
 * What the host managed to do with "discard session". `supported` is false when
 * the running API has no abandon route yet — the panel then says so instead of
 * pretending the session is gone.
 */
export interface RuntimeAbandonResult {
  abandoned: boolean;
  supported: boolean;
  detail: string;
}

export interface SessionRuntimePanelProps {
  model: RuntimeModel;
  unit: UnitSystem;
  /** `countdown` for a freshly started session, `resume` for one in progress. */
  mode: 'countdown' | 'resume';
  onLogSet: (movement: RuntimeMovement, setNumber: number, draft: SetDraft) => Promise<RuntimeWriteOutcome>;
  onPatchSet: (movement: RuntimeMovement, setNumber: number, draft: SetDraft) => Promise<RuntimeWriteOutcome>;
  onComplete: (score: RuntimeScore) => Promise<RuntimeCompletion | null>;
  /** Best effort: stamps the captured pain onto the sets this panel wrote. */
  onRecordPain?: (setIds: string[], painScore: number) => Promise<void>;
  /** Best effort: abandon a session the lifter no longer wants. */
  onAbandon?: () => Promise<RuntimeAbandonResult>;
  onClose: () => void;
  onAddMovement?: () => void;
}

/* ---------------------------------------------------------------------------
   Request bridges. A route that starts a session asks the Grid to open the panel
   once it has read the active session; the grid's play button asks a panel that
   is already mounted (and minimized) to come back to the same point.
   ------------------------------------------------------------------------ */

let pendingRequest: 'countdown' | 'resume' | null = null;

const openListeners = new Set<() => void>();

export function requestRuntimeOpen(mode: 'countdown' | 'resume'): void {
  pendingRequest = mode;
  // A host that is already mounted must react in place: navigating to a route
  // that is already on screen does not remount it, so a request that only a
  // fresh mount could see would strand the panel until a tab switch.
  for (const listener of openListeners) listener();
}

export function consumeRuntimeOpen(): 'countdown' | 'resume' | null {
  const value = pendingRequest;
  pendingRequest = null;
  return value;
}

/** Listen for an in-place open request (a request not consumed by a mount). */
export function subscribeRuntimeOpen(listener: () => void): () => void {
  openListeners.add(listener);
  return () => {
    openListeners.delete(listener);
  };
}

const expandListeners = new Set<() => void>();

/** Restore a mounted (possibly minimized) panel without resetting anything. */
export function requestRuntimeExpand(): void {
  for (const listener of expandListeners) listener();
}

/* ---------------------------------------------------------------------------
   Adapters
   ------------------------------------------------------------------------ */

export function runtimeSetFromWire(set: SessionSet): RuntimeSetState {
  return {
    id: set.id,
    setNumber: set.setIndex,
    weightKg: set.weightKg,
    reps: set.reps,
    rpe: set.rpe,
    measure: set.measure,
    distanceM: set.distanceM,
    durationS: set.durationS,
    status: set.status,
    isPr: set.isPr,
  };
}

export function runtimeTotalsFromWire(totals: SessionTotalsWire | null | undefined): RuntimeTotals | null {
  if (!totals) return null;
  return {
    volumeKg: totals.volumeKg,
    totalReps: totals.totalReps,
    setsCompleted: totals.setsCompleted,
    setsPrescribed: totals.setsPrescribed,
    prCount: totals.prCount,
    durationMin: totals.durationMin,
  };
}

export function runtimeModelFromPayload(
  payload: ActiveSessionPayload,
  previousByExercise: Map<string, string[]>,
): RuntimeModel {
  const session = payload.session;
  return {
    sessionId: session?.id ?? 'unknown-session',
    label: session?.label ?? null,
    startedAt: session?.startedAt ?? new Date().toISOString(),
    totals: runtimeTotalsFromWire(payload.totals),
    movements: payload.sessionExercises.map((exercise) => {
      const prescription = parsePrescription(exercise.prescription);
      const isWarmup = exercise.isWarmup === true;
      return {
        id: exercise.id,
        exerciseId: exercise.exerciseId,
        name: exercise.exerciseName,
        prescription,
        notes: exercise.notes ?? prescription.notes,
        isWarmup,
        previous: previousByExercise.get(exercise.exerciseId) ?? [],
        // A warm-up movement's sets are ALL warm-up sets; on a working movement
        // the warm-up-flagged sets stay out of the working slot list, exactly as
        // the server keeps them out of the metrics.
        sets: (isWarmup ? exercise.loggedSets : exercise.loggedSets.filter((set) => !set.isWarmup)).map(
          runtimeSetFromWire,
        ),
        source: exercise,
      };
    }),
  };
}

/* ---------------------------------------------------------------------------
   The quiet sync indicator — a queued set is honest, not hidden.

   A write that never reached the API is queued locally and replayed
   automatically; this says how many are waiting and clears itself the moment
   the replay lands. It is deliberately quiet: a line, not a failure toast.
   ------------------------------------------------------------------------ */

export function PendingSyncNotice({ className, testId = 'pending-sync' }: { className?: string; testId?: string }) {
  const [status, setStatus] = useState(() => pendingWriteStatus());
  useEffect(() => subscribePendingWrites(setStatus), []);
  if (status.pending === 0) return null;
  return (
    <p
      data-testid={testId}
      role="status"
      className={cn('text-[11px] font-bold uppercase tracking-[0.06em] text-warning', className)}
    >
      {pluralize(status.pending, 'set')} waiting to sync — it will be sent automatically.
    </p>
  );
}

/* ---------------------------------------------------------------------------
   Steps — one set at a time, in prescription then logged order
   ------------------------------------------------------------------------ */

interface RuntimeStep {
  movement: RuntimeMovement;
  setNumber: number;
  totalSets: number;
  logged: RuntimeSetState | null;
}

function setKey(movementId: string, setNumber: number): string {
  return `${movementId}:${setNumber}`;
}

function isSetComplete(set: RuntimeSetState | null): boolean {
  return set !== null && set.status !== 'pending';
}

/** Does the drum's draft already match the persisted set? (Rest edits reuse it.) */
function draftMatchesSet(draft: SetDraft, set: RuntimeSetState): boolean {
  const near = (a: number | null, b: number | null) =>
    (a === null && b === null) || (a !== null && b !== null && Math.abs(a - b) < 0.001);
  return (
    near(draft.weightKg, set.weightKg) &&
    near(draft.reps, set.reps) &&
    near(draft.rpe, set.rpe) &&
    near(draft.distanceM, set.distanceM) &&
    near(draft.durationS, set.durationS)
  );
}

function draftSignature(target: RuntimeStep, draft: SetDraft): string {
  return JSON.stringify([
    target.movement.id,
    target.setNumber,
    draft.weightKg,
    draft.reps,
    draft.rpe,
    draft.distanceM,
    draft.durationS,
  ]);
}

/** How many sets a movement runs: its prescription, its log, at least one. */
function movementTotalSets(movement: RuntimeMovement): number {
  return Math.max(movement.prescription.sets, movement.sets.length, 1);
}

/**
 * The one true order of the session, and the order every advance follows.
 *
 * Members of a superset / drop-set group are the movements sharing the same
 * non-null `supersetGroup` (a session is one plan day, so those rows are the
 * members), and a group ROTATES within itself before the set number advances:
 *
 *   set 1 ex1 → set 1 ex2 → set 1 ex3 → set 2 ex1 → set 2 ex2 → set 2 ex3 → …
 *
 * A member with fewer prescribed sets than the group's longest is skipped once
 * it is exhausted, and the group ends after its last round — the round in which
 * the last still-working member finishes. A drop-set group rotates identically.
 * An ungrouped movement keeps its sets consecutive, exactly as it always has,
 * and the next group starts only after the previous group's last round.
 *
 * Because the returned list is already in rotation order, `cursor ± 1` (the
 * automatic advance, `›` and `‹`) is the rotation with no further bookkeeping.
 */
function buildSteps(model: RuntimeModel, written: Map<string, RuntimeSetState>): RuntimeStep[] {
  const steps: RuntimeStep[] = [];
  const totals = new Map(model.movements.map((movement) => [movement.id, movementTotalSets(movement)]));

  const pushStep = (movement: RuntimeMovement, setNumber: number) => {
    const persisted = movement.sets.find((set) => set.setNumber === setNumber) ?? null;
    steps.push({
      movement,
      setNumber,
      totalSets: totals.get(movement.id) ?? 1,
      logged: written.get(setKey(movement.id, setNumber)) ?? persisted,
    });
  };

  const consumed = new Set<string>();
  for (const movement of model.movements) {
    if (consumed.has(movement.id)) continue;
    const group = movement.prescription.supersetGroup;
    if (group === null) {
      const total = totals.get(movement.id) ?? 1;
      for (let setNumber = 1; setNumber <= total; setNumber += 1) pushStep(movement, setNumber);
      continue;
    }
    // Every not-yet-consumed movement on this session sharing the group number,
    // in plan order; the triggering movement is always a member of its own group.
    const members = model.movements.filter(
      (entry) => !consumed.has(entry.id) && entry.prescription.supersetGroup === group,
    );
    const rounds = Math.max(...members.map((member) => totals.get(member.id) ?? 1));
    for (let setNumber = 1; setNumber <= rounds; setNumber += 1) {
      for (const member of members) {
        if ((totals.get(member.id) ?? 1) < setNumber) continue;
        pushStep(member, setNumber);
      }
    }
    for (const member of members) consumed.add(member.id);
  }
  return steps;
}

function firstIncompleteIndex(steps: RuntimeStep[]): number {
  const index = steps.findIndex((step) => !isSetComplete(step.logged));
  return index === -1 ? Math.max(0, steps.length - 1) : index;
}

function allComplete(steps: RuntimeStep[]): boolean {
  return steps.length > 0 && steps.every((step) => isSetComplete(step.logged));
}

/* ---------------------------------------------------------------------------
   Small helpers
   ------------------------------------------------------------------------ */

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

function formatWallClock(date: Date): string {
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function restWord(seconds: number): string {
  if (seconds <= 0) return 'none prescribed';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.round((seconds / 60) * 10) / 10} min`;
}

function unitLabel(unit: UnitSystem): string {
  return unit === 'imperial' ? 'lb' : 'kg';
}

function weightIncrement(unit: UnitSystem): number {
  return unit === 'imperial' ? 5 : 2.5;
}

function microIncrement(unit: UnitSystem): number {
  return unit === 'imperial' ? 0.5 : 0.25;
}

function snapDisplay(value: number): number {
  return Math.round(value * 2) / 2;
}

function readStoredTimes(sessionId: string): { exerciseMs: number; restMs: number } {
  try {
    const raw = window.localStorage.getItem(`l2f.runtime.times.${sessionId}`);
    if (!raw) return { exerciseMs: 0, restMs: 0 };
    const parsed = JSON.parse(raw) as { exerciseMs?: unknown; restMs?: unknown };
    const exerciseMs = typeof parsed.exerciseMs === 'number' && Number.isFinite(parsed.exerciseMs) ? Math.max(0, parsed.exerciseMs) : 0;
    const restMs = typeof parsed.restMs === 'number' && Number.isFinite(parsed.restMs) ? Math.max(0, parsed.restMs) : 0;
    return { exerciseMs, restMs };
  } catch {
    return { exerciseMs: 0, restMs: 0 };
  }
}

type Phase = 'ready' | 'countdown' | 'working' | 'resting' | 'finish' | 'capture' | 'complete';

const RPE_OPTIONS = ['5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10'];

/** The pre-set countdown and the rest run-in share one 10 → 1 window. */
export const COUNTDOWN_MS = 10_000;

/**
 * The pre-set countdown numeral, decided from ELAPSED time on a pure clock.
 * Extracted so the boundary is testable without a browser or a real 10 s wait.
 */
export function countdownFromElapsed(elapsedMs: number): number {
  return Math.max(1, Math.ceil((COUNTDOWN_MS - Math.max(0, elapsedMs)) / 1000));
}

export interface RestRunInState {
  /** The transport should show the 10 → 1 countdown instead of the rest clock. */
  active: boolean;
  remainingMs: number;
  countdownValue: number | null;
}

/**
 * Where the rest clock is, decided from the time REMAINING (`restSeconds -
 * elapsed <= 10`) — never from a threshold on elapsed time.
 *
 * The invariant is the whole point: a 210 s rest must flip at 200 s elapsed
 * (10 s left), not at 10 s elapsed (200 s early). Deriving from `remainingMs`
 * makes the flip land at `restSeconds - 10` for every rest: 210 → 200 s,
 * 20 → 10 s, 8 → 0 s. Extracted as a pure function so that boundary is
 * testable directly, with no 200 s browser wait and no clock drift.
 */
export function restRunInState(targetMs: number, elapsedMs: number): RestRunInState {
  const remainingMs = Math.max(0, targetMs - Math.max(0, elapsedMs));
  const active = targetMs > 0 && remainingMs <= COUNTDOWN_MS;
  return { active, remainingMs, countdownValue: active ? Math.max(1, Math.ceil(remainingMs / 1000)) : null };
}

/** How long the transport must be held to complete the running set. */
const HOLD_TO_DONE_MS = 5000;

/* ---------------------------------------------------------------------------
   The drum — which fields rotate, in which order
   ------------------------------------------------------------------------ */

type DrumField = 'weight' | 'reps' | 'rpe' | 'distance' | 'duration' | 'rest';

const FIELD_LABELS: Record<DrumField, string> = {
  weight: 'Weight',
  reps: 'Reps',
  rpe: 'RPE',
  distance: 'Distance',
  duration: 'Duration',
  rest: 'Rest',
};

const FIELD_SHORT: Record<DrumField, string> = {
  weight: 'Weight',
  reps: 'Reps',
  rpe: 'RPE',
  distance: 'Distance',
  duration: 'Duration',
  rest: 'Rest',
};

/** Weight, reps, RPE and this set's rest; a distance/time movement swaps load. */
function drumFields(measure: SetMeasure): DrumField[] {
  if (measure === 'distance_m') return ['distance', 'rpe', 'rest'];
  if (measure === 'time_s') return ['duration', 'rpe', 'rest'];
  return ['weight', 'reps', 'rpe', 'rest'];
}

function fieldUnit(field: DrumField, unit: UnitSystem): string | null {
  if (field === 'weight') return unitLabel(unit);
  if (field === 'distance') return 'm';
  if (field === 'duration' || field === 'rest') return 's';
  return null;
}

/**
 * The unit lives in the field NAME, never beside the value: `WEIGHT KG`,
 * `REST S`, `DISTANCE M`. The value itself is a bare number, so the value box
 * is the same width for `65`, `102.5`, `8` and `210`.
 */
function fieldLabel(field: DrumField, unit: UnitSystem): string {
  const suffix = fieldUnit(field, unit);
  return suffix ? `${FIELD_LABELS[field]} ${suffix.toUpperCase()}` : FIELD_LABELS[field];
}

/** Half-step RPE inside 1–10. */
function snapRpe(value: number): number {
  return Math.min(10, Math.max(1, Math.round(value * 2) / 2));
}

/**
 * The sound control is an icon, not a word: a speaker that carries a slash when
 * muted. It is a real button with a fixed box, so the header never reflows
 * between the two states.
 */
function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M11 5 6 9H3v6h3l5 4z" />
      {muted ? (
        <>
          <line x1="16" y1="9" x2="22" y2="15" />
          <line x1="22" y1="9" x2="16" y2="15" />
        </>
      ) : (
        <>
          <path d="M15.5 8.5a5 5 0 0 1 0 7" />
          <path d="M18.5 5.5a9 9 0 0 1 0 13" />
        </>
      )}
    </svg>
  );
}

/* ---------------------------------------------------------------------------
   The panel
   ------------------------------------------------------------------------ */

export function SessionRuntimePanel(props: SessionRuntimePanelProps) {
  const { model, unit, mode, onLogSet, onPatchSet, onComplete, onRecordPain, onAbandon, onClose, onAddMovement } = props;

  const [written, setWritten] = useState<Map<string, RuntimeSetState>>(() => new Map());
  const writtenRef = useRef(written);
  const steps = useMemo(() => buildSteps(model, written), [model, written]);

  const [phase, setPhase] = useState<Phase>(() => {
    if (steps.length === 0) return 'ready';
    if (allComplete(steps)) return 'capture';
    return 'ready';
  });
  const [cursor, setCursor] = useState(() => firstIncompleteIndex(steps));
  const [paused, setPaused] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [muted, setMuted] = useState(() => isRuntimeMuted());
  const [moodScore, setMoodScore] = useState<number | null>(null);
  const [rpeScore, setRpeScore] = useState<number | null>(null);
  const [painScore, setPainScore] = useState(0);
  const [completion, setCompletion] = useState<RuntimeCompletion | null>(null);
  const [announcement, setAnnouncement] = useState('');
  /** Press-and-hold progress on the transport: 0 → 1 over HOLD_TO_DONE_MS. */
  const [holdProgress, setHoldProgress] = useState(0);
  /** The rest target in force, in seconds — the value the rest clock counts to. */
  const [restTargetS, setRestTargetS] = useState(0);

  const panelRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLButtonElement>(null);
  const drumInputRef = useRef<HTMLInputElement>(null);
  const [, forceTick] = useState(0);
  const tick = useCallback(() => forceTick((value) => value + 1), []);

  /** A draft edited during rest, waiting to be PATCHed onto the completed set. */
  const restPendingDraft = useRef<{ step: RuntimeStep; draft: SetDraft } | null>(null);
  /** The signature last sent for that set, so a rejected PATCH is not retried. */
  const restEditSig = useRef('');

  const nowMs = useCallback(() => performance.now(), []);

  /* ------------------------------- timers -------------------------------- */

  const storedTimes = useRef(readStoredTimes(model.sessionId));
  /** Real, folded wall-clock durations: the numbers that persist. */
  const clock = useRef({ exerciseMs: storedTimes.current.exerciseMs, restMs: storedTimes.current.restMs });
  /** Wall-clock anchors for the CURRENT phase. Display and record share them. */
  const workStartedAt = useRef<number | null>(null);
  const restStartedAt = useRef<number | null>(null);
  const countdownStartedAt = useRef<number | null>(null);
  const pausedAt = useRef<number | null>(null);
  const pausedPhase = useRef<Phase | null>(null);
  /** Display-only held time per phase — the freeze, never the record. */
  const pausedHeld = useRef({ countdown: 0, work: 0, rest: 0 });
  const restTarget = useRef({ targetMs: 0 });
  const runInFired = useRef(false);

  /**
   * The rest that follows each step, keyed by `movement:set`. The rest a set
   * earns is owned by that set: a REST edit is stored here, so moving the cursor
   * with ‹/› can never silently reset the rest the completed set will use.
   */
  const restByStep = useRef<Map<string, number>>(new Map());
  /** The key of the step whose rest is running — only its REST field retargets. */
  const restStepKey = useRef('');
  /** The step on screen and the phase, readable from ref-only callbacks. */
  const stepRef = useRef<RuntimeStep | null>(null);
  const phaseRef = useRef<Phase>('ready');

  /** The recorded accumulators: real wall time, pause or not. */
  const liveExerciseMs = () =>
    clock.current.exerciseMs + (workStartedAt.current === null ? 0 : nowMs() - workStartedAt.current);
  const liveRestMs = () =>
    clock.current.restMs + (restStartedAt.current === null ? 0 : nowMs() - restStartedAt.current);

  const pausedNowMs = useCallback(
    () => (pausedAt.current === null ? 0 : nowMs() - pausedAt.current),
    [nowMs],
  );

  /**
   * The SET stopwatch as displayed. It counts from its own phase anchor, so it
   * starts at 00:00 for each set and each rest even though the recorded total is
   * cumulative. Paused time is subtracted from the display only.
   */
  const displayWorkMs = useCallback(
    () =>
      workStartedAt.current === null
        ? 0
        : Math.max(0, nowMs() - workStartedAt.current - pausedHeld.current.work - (phase === 'working' ? pausedNowMs() : 0)),
    [phase, nowMs, pausedNowMs],
  );
  const displayRestMs = useCallback(
    () =>
      restStartedAt.current === null
        ? 0
        : Math.max(0, nowMs() - restStartedAt.current - pausedHeld.current.rest - (phase === 'resting' ? pausedNowMs() : 0)),
    [phase, nowMs, pausedNowMs],
  );
  const displayCountdownElapsedMs = useCallback(
    () => Math.max(0, nowMs() - (countdownStartedAt.current ?? nowMs()) - pausedHeld.current.countdown - (phase === 'countdown' ? pausedNowMs() : 0)),
    [phase, nowMs, pausedNowMs],
  );

  const foldClocks = useCallback(() => {
    const now = nowMs();
    if (workStartedAt.current !== null) {
      clock.current.exerciseMs += now - workStartedAt.current;
      workStartedAt.current = null;
    }
    if (restStartedAt.current !== null) {
      clock.current.restMs += now - restStartedAt.current;
      restStartedAt.current = null;
    }
    countdownStartedAt.current = null;
    pausedAt.current = null;
    pausedPhase.current = null;
    pausedHeld.current = { countdown: 0, work: 0, rest: 0 };
  }, [nowMs]);

  const persistTimes = useCallback(() => {
    try {
      window.localStorage.setItem(
        `l2f.runtime.times.${model.sessionId}`,
        JSON.stringify({ exerciseMs: liveExerciseMs(), restMs: liveRestMs() }),
      );
    } catch {
      /* storage blocked — the timers still run for this sitting */
    }
  }, [model.sessionId]);

  const persistRef = useRef(persistTimes);
  persistRef.current = persistTimes;

  useEffect(() => {
    persistTimes();
  }, [phase, paused, persistTimes]);

  useEffect(() => {
    const id = window.setInterval(tick, 200);
    const onVisibility = () => tick();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tick]);

  useEffect(() => {
    const onUnload = () => persistRef.current();
    const onHide = () => persistRef.current();
    window.addEventListener('beforeunload', onUnload);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      document.removeEventListener('visibilitychange', onHide);
      persistRef.current();
    };
  }, []);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  useEffect(() => {
    const unlock = () => unlockRuntimeAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const play = useCallback((tone: RuntimeTone) => {
    playRuntimeTone(tone);
  }, []);

  const toggleMuted = () => {
    const next = !muted;
    setRuntimeMuted(next);
    setMuted(next);
    if (!next) unlockRuntimeAudio();
  };

  /* ---------------------------- phase changes ---------------------------- */

  const toReady = useCallback(() => {
    foldClocks();
    setPaused(false);
    setPhase('ready');
  }, [foldClocks]);

  const beginCountdown = useCallback(() => {
    foldClocks();
    countdownStartedAt.current = nowMs();
    pausedHeld.current.countdown = 0;
    setPaused(false);
    setPhase('countdown');
    setAnnouncement('Countdown. Ten seconds to the set.');
    play('countdownStart');
  }, [foldClocks, nowMs, play]);

  const beginWork = useCallback(() => {
    foldClocks();
    workStartedAt.current = nowMs();
    pausedHeld.current.work = 0;
    setPaused(false);
    setPhase('working');
    setAnnouncement('Set started.');
    play('go');
  }, [foldClocks, nowMs, play]);

  /**
   * Start the rest that follows the set just completed. `completed` is that set,
   * and `seconds` is ITS rest — never the next set's and never a default.
   */
  const beginRest = useCallback(
    (seconds: number, completed: RuntimeStep) => {
      foldClocks();
      const whole = Math.max(0, Math.round(seconds));
      const key = setKey(completed.movement.id, completed.setNumber);
      restByStep.current.set(key, whole);
      restStepKey.current = key;
      restTarget.current.targetMs = whole * 1000;
      setRestTargetS(whole);
      restStartedAt.current = nowMs();
      pausedHeld.current.rest = 0;
      runInFired.current = false;
      setPaused(false);
      setPhase('resting');
      setAnnouncement(`Resting. ${restWord(whole)} until the next set.`);
      play('restStart');
    },
    [foldClocks, nowMs, play],
  );

  /**
   * The advance happens at the END of the rest countdown (or when the lifter
   * presses "Start next set") — never at Done set. The just-completed set stays
   * on screen, editable, for the whole rest so it can be corrected. With no rest
   * prescribed the next set is armed with its own 10 → 1 countdown.
   */
  const advanceToNextSet = useCallback(
    (mode: 'work' | 'countdown') => {
      if (cursor >= steps.length - 1) {
        // No next set: conclude rather than silently finish — the finish prompt
        // asks for mood / exertion / pain first.
        foldClocks();
        setPaused(false);
        setPhase('finish');
        setAnnouncement('Last set logged. Finish the session?');
        return;
      }
      setCursor(cursor + 1);
      if (mode === 'countdown') beginCountdown();
      else beginWork();
    },
    [cursor, steps.length, foldClocks, beginCountdown, beginWork],
  );

  const togglePause = useCallback(() => {
    const now = nowMs();
    if (!paused) {
      pausedAt.current = now;
      pausedPhase.current = phase;
      setPaused(true);
      return;
    }
    const held = pausedAt.current === null ? 0 : now - pausedAt.current;
    const which = pausedPhase.current ?? phase;
    if (which === 'countdown') pausedHeld.current.countdown += held;
    else if (which === 'working') pausedHeld.current.work += held;
    else if (which === 'resting') pausedHeld.current.rest += held;
    pausedAt.current = null;
    pausedPhase.current = null;
    setPaused(false);
  }, [paused, phase, nowMs]);

  /** The transport: it starts the countdown from ready, then plays/pauses. */
  const onTransport = useCallback(() => {
    if (phase === 'ready') {
      beginCountdown();
      return;
    }
    togglePause();
  }, [phase, beginCountdown, togglePause]);

  /* --------------------------- derived clocks ---------------------------- */

  const countdownValue = phase === 'countdown' ? countdownFromElapsed(displayCountdownElapsedMs()) : null;
  const restDisplayMs = phase === 'resting' ? displayRestMs() : 0;
  const restRunIn = phase === 'resting' ? restRunInState(restTarget.current.targetMs, restDisplayMs) : null;
  const restRemainingMs = restRunIn?.remainingMs ?? 0;
  const runIn = restRunIn?.active ?? false;
  const runInValue = restRunIn?.countdownValue ?? null;

  /* --- audible + announced ticking (pre-set countdown and rest run-in) ----- */
  const lastTick = useRef<number | null>(null);
  const tickingValue = countdownValue ?? runInValue;
  useEffect(() => {
    if (tickingValue === null) {
      lastTick.current = null;
      return;
    }
    if (lastTick.current === tickingValue) return;
    const first = lastTick.current === null;
    lastTick.current = tickingValue;
    if (!first) play('tick');
    setAnnouncement(`${tickingValue}`);
  }, [tickingValue, play]);

  useEffect(() => {
    if (phase !== 'resting') {
      runInFired.current = false;
      return;
    }
    if (runIn && !runInFired.current) {
      runInFired.current = true;
      play('runIn');
      setAnnouncement(`Next set in ${runInValue ?? 0} seconds.`);
    }
  }, [phase, runIn, runInValue, play]);

  /* --- the countdown and the rest clock advance the state machine ----------- */
  useEffect(() => {
    if (phase !== 'countdown' && phase !== 'resting') return undefined;
    const id = window.setInterval(() => {
      if (phase === 'countdown') {
        if (displayCountdownElapsedMs() >= COUNTDOWN_MS) beginWork();
        return;
      }
      // The transport is frozen while paused, so this can never advance on hold.
      if (restTarget.current.targetMs - displayRestMs() <= 0) advanceToNextSet('work');
    }, 100);
    return () => window.clearInterval(id);
  }, [phase, beginWork, advanceToNextSet, displayCountdownElapsedMs, displayRestMs]);

  /* ----------------------- discard / cancel a session -------------------- */

  const [discardPrompt, setDiscardPrompt] = useState<null | 'cancel' | 'explicit'>(null);
  const [discardMessage, setDiscardMessage] = useState<string | null>(null);
  const [discardBusy, setDiscardBusy] = useState(false);
  const keepButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (discardPrompt) keepButtonRef.current?.focus();
  }, [discardPrompt]);

  /** Cancelling the countdown never silently throws the session away. */
  const offerDiscard = useCallback(() => {
    setDiscardMessage(null);
    setDiscardPrompt('cancel');
  }, []);

  const keepSession = useCallback(() => {
    const cancelled = discardPrompt === 'cancel';
    setDiscardPrompt(null);
    setDiscardMessage(null);
    if (cancelled) onClose();
  }, [discardPrompt, onClose]);

  const confirmDiscard = useCallback(async () => {
    if (!onAbandon) {
      setDiscardMessage('This host cannot discard a session yet, so nothing was changed.');
      return;
    }
    setDiscardBusy(true);
    setDiscardMessage(null);
    const result = await onAbandon();
    setDiscardBusy(false);
    if (result.abandoned) {
      setDiscardPrompt(null);
      setAnnouncement('Session discarded.');
      onClose();
      return;
    }
    setDiscardMessage(result.detail);
  }, [onAbandon, onClose]);

  /* --------------------------- sets and drafts --------------------------- */

  const safeCursor = Math.min(cursor, Math.max(0, steps.length - 1));
  const step = steps[safeCursor] ?? null;
  const movement = step?.movement ?? null;
  const prescription = movement?.prescription ?? null;
  // The warm-up session runs first (0019). These two numbers label the phase
  // ("Warm-up · 2 of 5") without re-reading the plan.
  const warmupMovements = model.movements.filter((entry) => entry.isWarmup);
  const warmupIndex =
    movement?.isWarmup === true ? warmupMovements.findIndex((entry) => entry.id === movement.id) + 1 : 0;
  // Mirror the render's step + phase into refs so ref-only callbacks (the REST
  // setter, Done set) always see the set that is actually on screen.
  stepRef.current = step;
  phaseRef.current = phase;

  const [weightKg, setWeightKg] = useState(0);
  const [reps, setReps] = useState(0);
  const [rpe, setRpe] = useState<number | null>(null);
  const [distanceM, setDistanceM] = useState(0);
  const [durationS, setDurationS] = useState(0);
  const [restS, setRestS] = useState(0);

  /** The canonical weight, mirrored in a ref so rapid ± taps never lose a step. */
  const weightKgRef = useRef(0);
  const repsRef = useRef(0);
  const rpeRef = useRef<number | null>(null);
  const distanceRef = useRef(0);
  const durationRef = useRef(0);
  const restSRef = useRef(0);

  const applyWeight = useCallback((canonical: number) => {
    const clamped = Math.max(0, canonical);
    weightKgRef.current = clamped;
    setWeightKg(clamped);
  }, []);

  const setRepsValue = useCallback((value: number) => {
    const clamped = Math.max(0, Math.round(value));
    repsRef.current = clamped;
    setReps(clamped);
  }, []);

  const setRpeValue = useCallback((value: number | null) => {
    const clamped = value === null ? null : snapRpe(value);
    rpeRef.current = clamped;
    setRpe(clamped);
  }, []);

  const setDistanceValue = useCallback((value: number) => {
    const clamped = Math.max(0, Math.round(value));
    distanceRef.current = clamped;
    setDistanceM(clamped);
  }, []);

  const setDurationValue = useCallback((value: number) => {
    const clamped = Math.max(0, Math.round(value));
    durationRef.current = clamped;
    setDurationS(clamped);
  }, []);

  /**
   * Write the REST field. The value is stored against the step it belongs to, so
   * it survives navigation; while that step's rest is running it is also the
   * live target — the field and the clock can never disagree. Seeding passes
   * `retarget: false` so re-seeding a step cannot disturb a running rest.
   */
  const setRestValue = useCallback((value: number, options?: { retarget?: boolean }) => {
    const clamped = Math.max(0, Math.round(value));
    restSRef.current = clamped;
    setRestS(clamped);
    const current = stepRef.current;
    const key = current ? setKey(current.movement.id, current.setNumber) : '';
    if (key) restByStep.current.set(key, clamped);
    if (activeFieldRef.current === 'rest') setDrumText(String(clamped));
    if ((options?.retarget ?? true) && phaseRef.current === 'resting' && key !== '' && key === restStepKey.current) {
      restTarget.current.targetMs = clamped * 1000;
      setRestTargetS(clamped);
      // A retargeted rest earns its run-in cue again: editing REST to lengthen
      // the rest must not swallow the next "next set in Ns" heads-up.
      runInFired.current = false;
    }
  }, []);

  const fields = useMemo(() => drumFields(prescription?.measure ?? 'reps'), [prescription?.measure]);

  const [activeField, setActiveField] = useState<DrumField>('weight');
  const activeFieldRef = useRef<DrumField>('weight');
  const [drumText, setDrumText] = useState('0');

  /** The active field's value as the lifter sees it (canonical kg → display). */
  const readFieldDisplay = useCallback(
    (field: DrumField): string => {
      switch (field) {
        case 'weight': {
          const display = kgToDisplay(weightKgRef.current, unit);
          return Number.isInteger(display) ? String(display) : String(Math.round(display * 100) / 100);
        }
        case 'reps':
          return String(repsRef.current);
        case 'rpe':
          return rpeRef.current === null ? '' : String(rpeRef.current);
        case 'distance':
          return String(distanceRef.current);
        case 'duration':
          return String(durationRef.current);
        case 'rest':
          return String(restSRef.current);
        default:
          return '';
      }
    },
    [unit],
  );

  /**
   * A visible roll when a new field reaches the middle. It animates the input
   * itself (not the measured value box), so the drum reads as ONE object turning
   * without moving any of the fixed boxes around it.
   */
  const rollDrumValue = useCallback(() => {
    const node = drumInputRef.current;
    if (!node || typeof node.animate !== 'function') return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    node.animate(
      [
        { transform: 'perspective(460px) rotateX(-68deg)', opacity: '0.2' },
        { transform: 'perspective(460px) rotateX(0deg)', opacity: '1' },
      ],
      { duration: 220, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
  }, []);

  const selectField = useCallback(
    (field: DrumField, announce = true) => {
      activeFieldRef.current = field;
      setActiveField(field);
      setDrumText(readFieldDisplay(field));
      rollDrumValue();
      if (announce) {
        setAnnouncement(`${fieldLabel(field, unit)} selected, ${readFieldDisplay(field) || 'not set'}.`);
      }
    },
    [readFieldDisplay, rollDrumValue, unit],
  );

  const rotateField = useCallback(
    (direction: 1 | -1) => {
      const list = fields;
      if (list.length === 0) return;
      const index = list.indexOf(activeFieldRef.current);
      const next = list[(index + direction + list.length) % list.length] ?? list[0];
      if (next) selectField(next);
    },
    [fields, selectField],
  );

  const stepKeyRef = useRef('');

  const seedDraft = useCallback(
    (target: RuntimeStep) => {
      const logged = target.logged;
      const earlier = target.movement.sets
        .filter((set) => set.setNumber < target.setNumber && set.status !== 'pending')
        .sort((a, b) => b.setNumber - a.setNumber)[0];
      const canonical =
        logged?.weightKg ??
        earlier?.weightKg ??
        target.movement.prescription.targetWeightKg ??
        0;
      const canonicalReps =
        logged?.reps ??
        earlier?.reps ??
        target.movement.prescription.repMax ??
        target.movement.prescription.repMin ??
        8;
      applyWeight(canonical);
      setRepsValue(canonicalReps);
      setRpeValue(logged?.rpe ?? earlier?.rpe ?? target.movement.prescription.targetRpe ?? null);
      setDistanceValue(logged?.distanceM ?? (target.movement.prescription.measure === 'distance_m' ? canonicalReps : 0));
      setDurationValue(logged?.durationS ?? (target.movement.prescription.measure === 'time_s' ? canonicalReps : 0));
      // The rest that follows THIS set: seed it from the prescription once, then
      // keep whatever the lifter edited for this set. Navigating away and back
      // must never hand the clock a different rest than the field shows.
      const restKey = setKey(target.movement.id, target.setNumber);
      if (!restByStep.current.has(restKey)) {
        restByStep.current.set(restKey, target.movement.prescription.restSeconds);
      }
      setRestValue(restByStep.current.get(restKey) ?? target.movement.prescription.restSeconds, { retarget: false });
      // The setters above write their refs synchronously, so the drum can seed
      // its text from the freshly updated values without waiting for a render.
      const first = drumFields(target.movement.prescription.measure)[0] ?? 'weight';
      activeFieldRef.current = first;
      setActiveField(first);
      setDrumText(readFieldDisplay(first));
    },
    [applyWeight, readFieldDisplay, setDistanceValue, setDurationValue, setRepsValue, setRestValue, setRpeValue],
  );

  useEffect(() => {
    const key = step ? setKey(step.movement.id, step.setNumber) : '';
    if (key === stepKeyRef.current) return;
    stepKeyRef.current = key;
    if (step) seedDraft(step);
  }, [step, seedDraft]);

  /** Parse whatever is in the drum and write it to the active field. */
  const commitDrumText = (raw?: string) => {
    const field = activeFieldRef.current;
    const source = raw ?? drumText;
    const parsed = Number.parseFloat(source);
    if (!Number.isFinite(parsed)) {
      setDrumText(readFieldDisplay(field));
      return;
    }
    switch (field) {
      case 'weight':
        applyWeight(displayToKg(parsed, unit));
        break;
      case 'reps':
        setRepsValue(parsed);
        break;
      case 'rpe':
        setRpeValue(parsed);
        break;
      case 'distance':
        setDistanceValue(parsed);
        break;
      case 'duration':
        setDurationValue(parsed);
        break;
      case 'rest':
        setRestValue(parsed);
        break;
      default:
        break;
    }
    setDrumText(readFieldDisplay(field));
  };

  const bumpWeight = (direction: -1 | 1, micro: boolean) => {
    const amount = micro ? microIncrement(unit) : weightIncrement(unit);
    const nextDisplay = snapDisplay(kgToDisplay(weightKgRef.current, unit) + direction * amount);
    applyWeight(displayToKg(nextDisplay, unit));
  };

  /** The one +/− pad: it always drives the PRIMARY (active) box. */
  const stepActive = (direction: -1 | 1, micro = false) => {
    const field = activeFieldRef.current;
    switch (field) {
      case 'weight':
        bumpWeight(direction, micro);
        break;
      case 'reps':
        setRepsValue(repsRef.current + direction);
        break;
      case 'rpe':
        setRpeValue((rpeRef.current ?? prescription?.targetRpe ?? 7) + direction * 0.5);
        break;
      case 'distance':
        setDistanceValue(distanceRef.current + direction * 5);
        break;
      case 'duration':
        setDurationValue(durationRef.current + direction * 5);
        break;
      case 'rest':
        setRestValue(restSRef.current + direction * 15);
        break;
      default:
        break;
    }
    const shown = readFieldDisplay(field);
    setAnnouncement(`${fieldLabel(field, unit)} ${shown || 'not set'}.`);
    setDrumText(shown);
  };

  const stepDescription = (field: DrumField, direction: -1 | 1): string => {
    switch (field) {
      case 'weight':
        return `${direction === 1 ? 'Increase' : 'Reduce'} weight by ${weightIncrement(unit)} ${unitLabel(unit)}`;
      case 'reps':
        return direction === 1 ? 'One more rep' : 'One fewer rep';
      case 'rpe':
        return `${direction === 1 ? 'Increase' : 'Reduce'} RPE by 0.5`;
      case 'distance':
        return `${direction === 1 ? 'Add' : 'Remove'} 5 metres`;
      case 'duration':
        return `${direction === 1 ? 'Add' : 'Remove'} 5 seconds of work`;
      case 'rest':
        return `${direction === 1 ? 'Add' : 'Remove'} 15 seconds of rest`;
      default:
        return direction === 1 ? 'Increase' : 'Reduce';
    }
  };

  /**
   * The set as the API must receive it: only the active measure's own fields,
   * and "not set" is `null`, never `0`. The API rejects `distanceM`/`durationS`
   * of 0 and an empty `tempo`.
   */
  const currentDraft = (): SetDraft => {
    const measure = prescription?.measure ?? 'reps';
    const repsValue = Math.max(0, Math.round(repsRef.current));
    return {
      weightKg: weightKgRef.current,
      reps: measure === 'reps' || measure === 'amrap' ? Math.max(1, repsValue) : repsValue,
      rpe: rpeRef.current,
      distanceM: measure === 'distance_m' ? (distanceRef.current > 0 ? distanceRef.current : null) : null,
      durationS: measure === 'time_s' ? (durationRef.current > 0 ? durationRef.current : null) : null,
      painScore: null,
    };
  };

  const rememberSet = useCallback((target: RuntimeStep, set: RuntimeSetState) => {
    const next = new Map(writtenRef.current);
    next.set(setKey(target.movement.id, target.setNumber), set);
    writtenRef.current = next;
    setWritten(next);
  }, []);

  /**
   * Write a rest-time correction back onto the set it belongs to. The set has
   * already been logged, so this is a PATCH, and its result is remembered so the
   * panel keeps showing the corrected values when the rest advances.
   */
  const persistRestEdit = useCallback(
    async (target: RuntimeStep, draft: SetDraft) => {
      setBusy(true);
      const result = await onPatchSet(target.movement, target.setNumber, draft);
      setBusy(false);
      if (isWriteFailure(result)) {
        setError(`That change did not reach your account — the set still holds its saved values. ${result.detail}`);
        return;
      }
      rememberSet(target, result.set);
      const load = result.set.weightKg > 0 ? `${formatWeight(result.set.weightKg, unit)} × ${result.set.reps}` : `BW × ${result.set.reps}`;
      setStatus(`Set ${target.setNumber} corrected during rest — ${load}.`);
    },
    [onPatchSet, rememberSet, unit],
  );

  /**
   * While resting, the just-completed set is still editable. Any change is
   * persisted (PATCH) after a short pause in typing; the pending draft is also
   * flushed when the rest ends, so an edit made in the last moment is never lost
   * when the panel advances.
   */
  useEffect(() => {
    if (phase !== 'resting' || !step || !step.logged || busy) return undefined;
    const draft = currentDraft();
    if (draftMatchesSet(draft, step.logged)) {
      restPendingDraft.current = null;
      restEditSig.current = '';
      return undefined;
    }
    restPendingDraft.current = { step, draft };
    const signature = draftSignature(step, draft);
    if (signature === restEditSig.current) return undefined;
    const id = window.setTimeout(() => {
      restEditSig.current = signature;
      const pending = restPendingDraft.current;
      restPendingDraft.current = null;
      if (pending) void persistRestEdit(pending.step, pending.draft);
    }, 600);
    return () => window.clearTimeout(id);
    // The value states are the edit signal; currentDraft reads them through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, step, weightKg, reps, rpe, distanceM, durationS, busy, persistRestEdit]);

  useEffect(() => {
    if (phase === 'resting') return;
    const pending = restPendingDraft.current;
    if (!pending) return;
    restPendingDraft.current = null;
    restEditSig.current = '';
    void persistRestEdit(pending.step, pending.draft);
  }, [phase, persistRestEdit]);

  const completeCurrentSet = useCallback(async () => {
    if (!step || !prescription) return;
    const draft = currentDraft();
    const measure = prescription.measure;
    if ((measure === 'reps' || measure === 'amrap') && draft.reps <= 0) {
      setError('A reps set needs at least one rep.');
      return;
    }
    if (measure === 'distance_m' && (draft.distanceM === null || draft.distanceM <= 0)) {
      setError('A distance set needs a distance greater than zero before it can be saved.');
      return;
    }
    if (measure === 'time_s' && (draft.durationS === null || draft.durationS <= 0)) {
      setError('A timed set needs a duration greater than zero before it can be saved.');
      return;
    }
    setBusy(true);
    setError(null);
    const result = step.logged
      ? await onPatchSet(step.movement, step.setNumber, draft)
      : await onLogSet(step.movement, step.setNumber, draft);
    setBusy(false);
    if (isWriteFailure(result)) {
      setError(`That set did not reach your account — it was NOT saved. ${result.detail}`);
      return;
    }
    rememberSet(step, result.set);
    const load = result.set.weightKg > 0 ? `${formatWeight(result.set.weightKg, unit)} × ${result.set.reps}` : `BW × ${result.set.reps}`;
    setStatus(`Set ${step.setNumber} of ${step.totalSets} logged — ${load}${result.isPr ? ' · personal record' : ''}.`);
    setAnnouncement(`Set ${step.setNumber} logged at ${load}.`);

    const isLast = safeCursor >= steps.length - 1;
    if (isLast) {
      // Never finish silently: the last set opens the "Finish the session?"
      // offer, so the lifter can add mood / RPE / pain first — or keep training.
      foldClocks();
      setPaused(false);
      setPhase('finish');
      setAnnouncement('Last set logged. Finish the session?');
      return;
    }
    // Done set ends the set and STARTS THE REST. It does not advance: the panel
    // keeps showing this set (still editable) until the rest countdown ends. The
    // rest is THIS set's own stored rest — its prescription rest, or the value
    // the lifter edited onto this set — never the next set's and never a default.
    const completedRest = restByStep.current.get(setKey(step.movement.id, step.setNumber)) ?? restSRef.current;
    if (completedRest > 0) beginRest(completedRest, step);
    else advanceToNextSet('countdown');
  }, [
    step,
    prescription,
    safeCursor,
    steps.length,
    unit,
    onLogSet,
    onPatchSet,
    rememberSet,
    foldClocks,
    beginRest,
    advanceToNextSet,
  ]);

  /* ---------------------- press-and-hold = Done set ---------------------- */

  const holdTimer = useRef<number | null>(null);
  const holdStartedAt = useRef<number | null>(null);
  const holdCompleted = useRef(false);
  const enterHeld = useRef(false);

  const cancelHold = useCallback(() => {
    if (holdTimer.current !== null) {
      window.clearInterval(holdTimer.current);
      holdTimer.current = null;
    }
    holdStartedAt.current = null;
    setHoldProgress(0);
  }, []);

  /** A deliberate ~5 s hold on the transport completes the set; a tap plays/pauses. */
  const startHold = useCallback(() => {
    // Only while a set is actually running — never during rest or the countdown.
    if (phase !== 'working' || busy || holdTimer.current !== null) return;
    holdCompleted.current = false;
    holdStartedAt.current = nowMs();
    setHoldProgress(0.01);
    holdTimer.current = window.setInterval(() => {
      const started = holdStartedAt.current;
      if (started === null) return;
      const progress = Math.min(1, (nowMs() - started) / HOLD_TO_DONE_MS);
      setHoldProgress(progress);
      if (progress >= 1) {
        holdCompleted.current = true;
        cancelHold();
        setAnnouncement('Set completed by hold.');
        void completeCurrentSet();
      }
    }, 50);
  }, [phase, busy, nowMs, cancelHold, completeCurrentSet]);

  const finishSession = useCallback(async () => {
    setBusy(true);
    setError(null);
    // Sync anything waiting before the session is written, so the history it
    // lands in already carries every set.
    await flushPendingWrites();
    const setIds = [...writtenRef.current.values()]
      .map((set) => set.id)
      .filter((id): id is string => typeof id === 'string' && !id.startsWith('pending:'));
    if (onRecordPain && painScore > 0 && setIds.length > 0) {
      try {
        await onRecordPain(setIds, painScore);
      } catch {
        /* the session completion is the important write; pain is best effort */
      }
    }
    const result = await onComplete({ perceivedExertion: rpeScore, moodScore, painScore: painScore > 0 ? painScore : null });
    setBusy(false);
    if (!result) {
      setError('The session did not reach your account, so it is still in progress. Try again.');
      return;
    }
    setCompletion(result);
    play('complete');
    setAnnouncement('Session complete.');
    setPhase('complete');
  }, [moodScore, rpeScore, painScore, onComplete, onRecordPain, play]);

  /* ------------------------------- navigation ---------------------------- */

  const goPrev = useCallback(() => {
    if (safeCursor <= 0) return;
    toReady();
    setCursor(safeCursor - 1);
  }, [safeCursor, toReady]);

  /**
   * The plain "next step" move: the next step in the group-rotated order, or,
   * after the very last set, the finish prompt — never a silent end and never a
   * disabled dead end.
   */
  const goNext = useCallback(() => {
    if (safeCursor >= steps.length - 1) {
      foldClocks();
      setPaused(false);
      setPhase('finish');
      setAnnouncement('Last set. Finish the session?');
      return;
    }
    toReady();
    setCursor(safeCursor + 1);
  }, [safeCursor, steps.length, toReady, foldClocks]);

  /**
   * `›` (and →) is context aware, so no extra action button is needed anywhere:
   *  - countdown → stop it and start the set now (the clock starts now);
   *  - rest      → skip the remaining rest and start the next round at once (the
   *                rest already run is still recorded as rest time);
   *  - a set     → the next step in the group-rotated order;
   *  - last set  → the finish prompt.
   * `‹` (and ←) is the unchanged reverse, always landing in Ready.
   */
  const onNext = useCallback(() => {
    if (phase === 'countdown') {
      beginWork();
      return;
    }
    if (phase === 'resting') {
      setAnnouncement('Rest skipped — next set.');
      advanceToNextSet('work');
      return;
    }
    if (phase === 'finish' || phase === 'capture' || phase === 'complete') return;
    goNext();
  }, [phase, beginWork, advanceToNextSet, goNext]);

  /* ------------------------------- drag/sweep ---------------------------- */

  const swipeRef = useRef<{ id: number; x: number; y: number } | null>(null);
  /** A completed sweep must not also fire the click on whatever ended under it. */
  const sweepFiredRef = useRef(false);

  /**
   * The sweep is tracked WITHOUT capturing the pointer. Capturing on pointerdown
   * retargets the subsequent pointerup to the well, so the browser fires `click`
   * on the well instead of on the chip or neighbour the finger landed on — which
   * is exactly why the field chips stopped working. Tracking (and only then
   * suppressing the click of a real drag) keeps both the taps and the sweep.
   */
  const onDrumPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    swipeRef.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
    sweepFiredRef.current = false;
  };

  const onDrumPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = swipeRef.current;
    if (!start || start.id !== event.pointerId) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    // A vertical gesture only: it must beat the horizontal travel so a sideways
    // drag never turns the drum.
    if (Math.abs(dy) < 28 || Math.abs(dy) <= Math.abs(dx)) return;
    // One rotation per gesture: dragging UP rolls the field below into the
    // middle (the drum turns forward), dragging DOWN brings the field above in.
    swipeRef.current = null;
    sweepFiredRef.current = true;
    rotateField(dy < 0 ? 1 : -1);
  };

  const onDrumPointerUp = () => {
    swipeRef.current = null;
  };

  const onDrumClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!sweepFiredRef.current) return;
    sweepFiredRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /* ------------------------------- minimize ------------------------------ */

  const minimize = useCallback(() => {
    setMinimized(true);
    setAnnouncement('Panel minimized — the session keeps running.');
  }, []);

  const expand = useCallback(() => {
    setMinimized(false);
    setAnnouncement('Panel restored.');
  }, []);

  useEffect(() => {
    expandListeners.add(expand);
    return () => {
      expandListeners.delete(expand);
    };
  }, [expand]);

  useEffect(() => {
    if (minimized) dockRef.current?.focus();
    else panelRef.current?.focus();
  }, [minimized]);

  /** `M` (and Escape) restores the panel while it is docked. The listener is
      attached on the next task so the very keypress that minimized the panel
      cannot immediately toggle it back. */
  useEffect(() => {
    if (!minimized) return undefined;
    let onKey: ((event: KeyboardEvent) => void) | null = null;
    const id = window.setTimeout(() => {
      onKey = (event: KeyboardEvent) => {
        if (event.key === 'm' || event.key === 'M' || event.key === 'Escape') {
          event.preventDefault();
          expand();
        }
      };
      window.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      window.clearTimeout(id);
      if (onKey) window.removeEventListener('keydown', onKey);
    };
  }, [minimized, expand]);

  /* ------------------------------- keyboard ------------------------------ */

  /** The drum is on screen (and therefore keyboard-operable) in these phases. */
  const drumLive = phase === 'ready' || phase === 'countdown' || phase === 'working' || phase === 'resting';

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const typing = target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA';
    /** The drum's own value field: it must still rotate the drum while focused. */
    const drumInput = target instanceof HTMLInputElement && target.id === 'runtime-drum-input';

    if (discardPrompt) return;

    if (event.key === 'm' || event.key === 'M') {
      if (typing) return;
      event.preventDefault();
      minimize();
      return;
    }
    if (event.key === 'Escape') {
      if (typing) return;
      event.preventDefault();
      // Escape minimizes rather than losing sight of a running session; the
      // header's close button is the explicit way to dismiss the panel.
      minimize();
      return;
    }
    if (typing) {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitDrumText();
        // The drum's own field keeps Enter as the transport key too (tap =
        // play/pause, hold = Done set). Any other typing input stops here.
        if (!drumInput) return;
      } else if (
        drumInput &&
        (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Home' || event.key === 'End')
      ) {
        // Fall through to the rotation switch: the drum rolls even while the
        // value field has focus, which is where the caret usually sits.
      } else {
        return;
      }
    }
    switch (event.key) {
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        onTransport();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        goPrev();
        break;
      case '<':
        event.preventDefault();
        goPrev();
        break;
      case 'ArrowRight':
        event.preventDefault();
        onNext();
        break;
      case '>':
        event.preventDefault();
        onNext();
        break;
      case 'ArrowUp':
        if (!drumLive) return;
        event.preventDefault();
        rotateField(-1);
        break;
      case 'ArrowDown':
        if (!drumLive) return;
        event.preventDefault();
        rotateField(1);
        break;
      case 'Home':
        if (!drumLive) return;
        event.preventDefault();
        rotateField(-1);
        break;
      case 'End':
        if (!drumLive) return;
        event.preventDefault();
        rotateField(1);
        break;
      case '+':
      case '=':
        if (!drumLive) return;
        event.preventDefault();
        stepActive(1);
        break;
      case '-':
      case '_':
        if (!drumLive) return;
        event.preventDefault();
        stepActive(-1);
        break;
      case 'Enter':
        event.preventDefault();
        if (phase === 'capture') {
          void finishSession();
          break;
        }
        if (phase === 'finish') {
          setPhase('capture');
          break;
        }
        if (phase === 'complete') {
          onClose();
          break;
        }
        if (event.repeat) break;
        // Enter is the transport key, exactly like Space: a tap plays/pauses,
        // and a deliberate hold completes the running set (keyup decides which).
        enterHeld.current = true;
        startHold();
        break;
      default:
        break;
    }
  };

  /**
   * Enter's release decides tap vs hold: a hold that reached `Done set` must not
   * then also toggle the transport, and a tap is the play/pause the RULES ask for.
   */
  const onKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || !enterHeld.current) return;
    enterHeld.current = false;
    if (holdCompleted.current) {
      holdCompleted.current = false;
      return;
    }
    cancelHold();
    if (phase === 'capture' || phase === 'finish' || phase === 'complete') return;
    onTransport();
  };

  /* ------------------------------- derivations --------------------------- */

  const partners = useMemo(() => {
    if (!prescription || prescription.supersetGroup === null) return [];
    return model.movements
      .filter((entry) => entry.id !== movement?.id && entry.prescription.supersetGroup === prescription.supersetGroup)
      .map((entry) => entry.name);
  }, [model.movements, prescription, movement?.id]);

  const totalSets = steps.reduce((sum, entry) => sum + entry.totalSets, 0);
  const summary = completion?.totals ?? model.totals;
  const completedSetTargets = useMemo(
    () =>
      model.movements.flatMap((entry) =>
        entry.sets
          .filter((set) => set.status !== 'pending')
          .map((set) => ({ measure: set.measure, weightKg: set.weightKg, reps: set.reps })),
      ),
    [model.movements],
  );
  const previousForMovement = movement?.previous ?? [];
  const sessionDurationMs = Date.now() - Date.parse(model.startedAt);
  const wallClock = formatWallClock(new Date());
  const workoutMs = Number.isFinite(sessionDurationMs) ? Math.max(0, sessionDurationMs) : 0;

  /* ------------------------------- rendering ----------------------------- */

  const chromeButton =
    'inline-flex items-center justify-center gap-1.5 rounded-pill border border-brand-white/25 px-3 text-[11px] font-bold uppercase tracking-[0.08em] text-brand-white transition-colors duration-150 hover:border-accent hover:text-accent disabled:opacity-40';

  const header = (
    <div className="flex items-center justify-between gap-2 border-b border-brand-white/10 px-4 py-2.5">
      <span className="flex min-w-0 items-center gap-2">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-accent text-on-accent" aria-hidden="true">
          <TimerIcon className="text-[13px]" />
        </span>
        <span className="min-w-0 truncate text-[11px] font-bold uppercase tracking-[0.1em] text-brand-white/70">
          {model.label ?? 'Session'}
          <span data-testid="runtime-session-plan">
            {warmupMovements.length > 0 ? ': warm-up session + session' : ' · runtime'}
          </span>
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        <button
          type="button"
          data-testid="runtime-mute"
          data-muted={muted ? 'true' : 'false'}
          aria-pressed={muted}
          aria-label={muted ? 'Unmute runtime sounds' : 'Mute runtime sounds'}
          title={muted ? 'Unmute runtime sounds' : 'Mute runtime sounds'}
          onClick={toggleMuted}
          className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-brand-white/25 text-brand-white/80 transition-colors duration-150 hover:border-accent hover:text-accent"
        >
          <SpeakerIcon muted={muted} />
        </button>
        {phase !== 'capture' && phase !== 'complete' ? (
          <button
            type="button"
            data-testid="runtime-discard"
            aria-label="Discard this session"
            onClick={() => {
              setDiscardMessage(null);
              setDiscardPrompt('explicit');
            }}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-white/25 text-brand-white/70 hover:border-danger hover:text-danger"
          >
            <TrashIcon className="text-sm" />
          </button>
        ) : null}
        <button
          type="button"
          data-testid="runtime-minimize"
          aria-label="Minimize the session panel"
          onClick={minimize}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-white/25 text-brand-white/80 hover:border-accent hover:text-accent"
        >
          <ChevronDownIcon className="text-sm" />
        </button>
        <button
          type="button"
          data-testid="runtime-close"
          aria-label="Close the runtime panel — the session keeps running"
          onClick={onClose}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-white/25 text-brand-white/80 hover:border-accent hover:text-accent"
        >
          <CloseIcon className="text-sm" />
        </button>
      </span>
    </div>
  );

  /**
   * THE TIMER STRIP — one row, one baseline: the wall clock (left), the SET
   * timer (centre, the anchor) and the continuous SESSION timer (right). The
   * 10 → 1 countdown (pre-set and rest run-in) replaces the SET timer in the
   * SAME cell: same box, same font size, no second row and no second panel.
   */
  const countdownShown =
    phase === 'countdown' ? (countdownValue ?? 1) : phase === 'resting' && runIn ? (runInValue ?? 1) : null;
  const transportSymbol = phase === 'ready' || paused ? '▶' : '⏸';
  const setClockText =
    phase === 'working'
      ? formatClock(displayWorkMs())
      : phase === 'resting'
        ? formatClock(restDisplayMs)
        : formatClock(0);

  const workoutStatus: 'READY' | 'COUNTDOWN' | 'SET' | 'REST' | null =
    phase === 'finish' || phase === 'capture' || phase === 'complete'
      ? null
      : phase === 'resting'
        ? 'REST'
        : phase === 'countdown'
          ? 'COUNTDOWN'
          : phase === 'ready'
            ? 'READY'
            : 'SET';

  /**
   * The rest run-in cue: "Next set in Ns", in the state line's reserved slot so
   * its appearance shifts nothing. It is the warning-amber heads-up, not an
   * accent-coloured primary action.
   */
  const runInBanner =
    phase === 'resting' && runIn ? (
      <span
        data-testid="runtime-runin"
        role="status"
        className="inline-flex items-center gap-1.5 rounded-pill bg-warning px-2.5 py-[2px] text-[11px] font-black uppercase tracking-[0.08em] text-brand-charcoal"
      >
        <AlertIcon className="text-sm" />
        Next set in {runInValue ?? 0}s
      </span>
    ) : null;

  const topStrip = (
    <dl
      data-testid="runtime-top-strip"
      aria-label="Current time, the set timer and the session timer"
      className="flex h-[72px] items-end justify-between gap-0 text-center"
    >
      <div className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1 px-2 pb-2">
        <dt className="text-[9px] font-bold uppercase tracking-[0.14em] text-brand-white/50">Now</dt>
        <dd data-testid="runtime-wall-clock" className="l2f-tabular font-mono text-[15px] font-bold leading-none">
          {wallClock}
        </dd>
      </div>
      <div
        data-testid="runtime-set-cell"
        data-set-cell
        className="flex h-full w-[150px] shrink-0 flex-col items-center justify-end gap-0.5 pb-1"
      >
        <dt className="text-[9px] font-bold uppercase tracking-[0.14em] text-brand-white/50">Set</dt>
        <dd
          data-testid="runtime-set-timer-cell"
          className="l2f-tabular flex h-[38px] items-center justify-center font-mono text-[30px] font-bold leading-none text-brand-white"
        >
          {countdownShown !== null ? (
            countdownNumeral(countdownShown)
          ) : (
            <span data-testid="runtime-set-timer">{setClockText}</span>
          )}
        </dd>
      </div>
      <div className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1 px-2 pb-2">
        <dt className="text-[9px] font-bold uppercase tracking-[0.14em] text-brand-white/50">Session</dt>
        <dd
          data-testid="runtime-session-timer"
          data-paused={paused ? 'true' : 'false'}
          aria-label="Session timer, running since the session started"
          className="l2f-tabular font-mono text-[15px] font-bold leading-none text-brand-white"
        >
          {formatClock(workoutMs)}
        </dd>
      </div>
    </dl>
  );

  /**
   * THE WORKOUT STATE — `SET` / `REST` / `COUNTDOWN` / `READY`, one word, in a
   * fixed-height line under the strip so it is unmistakable at a glance and
   * never competes with the set timer or moves the panel. The rest run-in cue
   * shares the line's reserved slot.
   */
  const stateLine = (
    <div data-testid="runtime-state-line" className="flex h-[26px] shrink-0 items-center justify-center gap-2">
      <span
        data-testid="runtime-status"
        data-status={workoutStatus ? workoutStatus.toLowerCase() : 'none'}
        aria-label={workoutStatus ? `Workout status: ${workoutStatus}` : undefined}
        className={cn(
          'inline-flex h-[20px] items-center rounded-pill px-3 font-display text-[12px] font-black uppercase leading-none tracking-[0.18em]',
          workoutStatus === 'REST'
            ? 'bg-warning/20 text-warning'
            : workoutStatus === 'COUNTDOWN'
              ? 'bg-accent/20 text-accent'
              : 'bg-brand-white/10 text-brand-white',
        )}
      >
        {workoutStatus ?? (
          <span aria-hidden="true" className="text-brand-white/30">
            —
          </span>
        )}
      </span>
      {phase === 'resting' && runIn ? runInBanner : null}
    </div>
  );

  /**
   * The recorded exercise/rest durations are still tracked in full (they feed the
   * summary and localStorage) but no longer rendered as dedicated rows — the
   * panel is shorter without them. Assistive tech and the checks read the same
   * live values here.
   */
  const recordedClocks = (
    <span className="sr-only" data-testid="runtime-recorded">
      <span data-testid="runtime-work-total">{formatClock(liveExerciseMs())}</span>
      <span data-testid="runtime-rest-total">{formatClock(liveRestMs())}</span>
    </span>
  );

  const liveRegions = (
    <>
      <p className="sr-only" aria-live="assertive" data-testid="runtime-announcer">
        {announcement}
      </p>
      <p className="sr-only" aria-live="polite">
        {status ?? ''}
      </p>
    </>
  );

  const emptyState = (
    <div className="flex flex-col items-center gap-3 px-6 py-10 text-center" data-testid="runtime-empty">
      <AlertIcon className="text-2xl text-accent" />
      <h2 className="font-display text-[18px] font-black uppercase tracking-[-0.01em] text-brand-white">
        No movements prescribed
      </h2>
      <p className="max-w-[34ch] text-[13px] text-brand-white/70">
        Add a movement to this session and the runtime fills itself from the prescription.
      </p>
      {onAddMovement ? (
        <button type="button" onClick={onAddMovement} className="inline-flex h-11 items-center gap-2 rounded-pill bg-accent px-5 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover">
          <PlusIcon className="text-sm" /> Add a movement
        </button>
      ) : null}
    </div>
  );

  /**
   * The countdown numeral. It replaces the SET timer in the strip's centre cell,
   * with 5 → 1 bolder and accent-coloured at the SAME font size as 10 → 6: only
   * weight and colour change, so the swap cannot resize the cell or the row.
   */
  function countdownNumeral(value: number) {
    const tail = value <= 5;
    return (
      <span
        data-testid="runtime-countdown-value"
        data-countdown-tail={tail ? 'true' : 'false'}
        className={cn(
          'l2f-tabular relative font-display text-[30px] leading-none tracking-[-0.04em]',
          tail ? 'l2f-runtime-pulse font-black text-accent' : 'font-semibold text-brand-white/90',
        )}
      >
        {value}
      </span>
    );
  }

  const setProgress = step ? (
    <ol className="flex flex-wrap items-center justify-center gap-1" aria-label={`Set ${step.setNumber} of ${step.totalSets}`}>
      {Array.from({ length: step.totalSets }, (_, index) => {
        const position = index + 1;
        return (
          <li
            key={position}
            aria-hidden="true"
            className={cn(
              'h-1.5 w-5 rounded-pill',
              position < step.setNumber ? 'bg-success' : position === step.setNumber ? 'bg-accent' : 'bg-brand-white/20',
            )}
          />
        );
      })}
    </ol>
  ) : null;

  const activeIndex = Math.max(0, fields.indexOf(activeField));
  const aboveField = fields.length > 1 ? (fields[(activeIndex - 1 + fields.length) % fields.length] ?? null) : null;
  const belowField = fields.length > 1 ? (fields[(activeIndex + 1) % fields.length] ?? null) : null;

  /* The VERTICAL three-field drum: the active field in the MIDDLE, the previous
     field above and the next below, dimmed and angled so the stack reads as one
     cylinder about to roll. `‹`/`›` already own previous/next SET, so the drum is
     turned on the vertical axis instead: ↑/↓, a vertical sweep, or a tap on a
     neighbour. No field, peek or pad carries a border — only the play/pause does.
     Every box is a fixed size, so rotating fields, a longer value or a SET↔REST
     flip never moves anything. */
  const drum = step && prescription ? (
    <div
      className="l2f-drum-well l2f-drum-sweep flex w-full min-w-0 flex-col items-center gap-1 px-2 py-1"
      role="group"
      aria-label="This set's values"
      draggable={false}
      onDragStart={(event) => event.preventDefault()}
      onPointerDown={onDrumPointerDown}
      onPointerMove={onDrumPointerMove}
      onPointerUp={onDrumPointerUp}
      onPointerLeave={onDrumPointerUp}
      onPointerCancel={onDrumPointerUp}
      onClickCapture={onDrumClickCapture}
    >
      <div
        data-testid="runtime-field-primary"
        data-active-field={activeField}
        className="l2f-drum-primary flex w-full min-w-0 shrink-0 flex-col items-center justify-center gap-1"
      >
        {aboveField ? (
          <button
            type="button"
            data-testid="runtime-field-prev"
            data-peek-field={aboveField}
            data-peek-side="up"
            onClick={() => selectField(aboveField)}
            aria-label={`${fieldLabel(aboveField, unit)}: ${readFieldDisplay(aboveField) || 'not set'}. Activate to bring it to the middle.`}
            className="l2f-drum-peek flex h-[54px] w-[172px] shrink-0 flex-col items-center justify-center gap-0.5"
          >
            <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-brand-white/60">
              {fieldLabel(aboveField, unit)}
            </span>
            <span
              key={aboveField}
              data-testid="runtime-field-prev-value"
              className="l2f-drum-roll l2f-tabular font-mono text-[16px] font-bold leading-none text-brand-white/70"
            >
              {readFieldDisplay(aboveField) || '—'}
            </span>
          </button>
        ) : (
          <span aria-hidden="true" className="h-[54px] w-[172px] shrink-0" />
        )}

        <div className="flex min-w-0 shrink-0 flex-col items-center justify-center gap-0.5">
          <button
            type="button"
            data-testid="runtime-field-label"
            onClick={() => rotateField(1)}
            aria-label={`Primary field: ${fieldLabel(activeField, unit)}. Activate to rotate to the next field.`}
            className="flex h-[20px] items-center gap-1.5 rounded-pill px-3 text-[11px] font-bold uppercase tracking-[0.16em] text-brand-white/80 hover:text-accent"
          >
            <span key={activeField} className="l2f-drum-field">
              {fieldLabel(activeField, unit)}
            </span>
            <span aria-hidden="true" className="text-[9px] text-brand-white/40">
              ⇅
            </span>
          </button>

          <span
            data-testid="runtime-value-box"
            data-weight-kg={weightKg}
            data-active-field={activeField}
            data-rest-target-s={activeField === 'rest' && phase === 'resting' ? restTargetS : undefined}
            className="flex h-[44px] w-[132px] shrink-0 items-center justify-center"
          >
            <label className="sr-only" htmlFor="runtime-drum-input">
              {fieldLabel(activeField, unit)}
            </label>
            <input
              id="runtime-drum-input"
              ref={drumInputRef}
              data-testid={`runtime-${activeField}-input`}
              data-active-field={activeField}
              type="text"
              inputMode={activeField === 'weight' || activeField === 'rpe' || activeField === 'distance' ? 'decimal' : 'numeric'}
              value={drumText}
              draggable={false}
              onDragStart={(event) => event.preventDefault()}
              onChange={(event) => setDrumText(event.target.value)}
              onBlur={(event) => commitDrumText(event.target.value)}
              onFocus={(event) => event.target.select()}
              aria-label={`${fieldLabel(activeField, unit)} value`}
              className="l2f-tabular h-full w-full bg-transparent text-center font-mono text-[28px] font-bold leading-none text-brand-white outline-none"
            />
          </span>
        </div>

        {belowField ? (
          <button
            type="button"
            data-testid="runtime-field-next"
            data-next-field={belowField}
            data-peek-field={belowField}
            data-peek-side="down"
            onClick={() => selectField(belowField)}
            aria-label={`${fieldLabel(belowField, unit)}: ${readFieldDisplay(belowField) || 'not set'}. Activate to bring it to the middle.`}
            className="l2f-drum-peek flex h-[54px] w-[172px] shrink-0 flex-col items-center justify-center gap-0.5"
          >
            <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-brand-white/60">
              {fieldLabel(belowField, unit)}
            </span>
            <span
              key={belowField}
              data-testid="runtime-field-next-value"
              className="l2f-drum-roll l2f-tabular font-mono text-[16px] font-bold leading-none text-brand-white/70"
            >
              {readFieldDisplay(belowField) || '—'}
            </span>
          </button>
        ) : (
          <span aria-hidden="true" className="h-[54px] w-[172px] shrink-0" />
        )}
      </div>

      <div role="group" aria-label="Choose a value to change" className="flex flex-wrap items-center justify-center gap-1.5">
        {fields.map((field) => (
          <button
            key={field}
            type="button"
            data-testid={`runtime-field-${field}`}
            aria-pressed={activeField === field}
            aria-label={`Show ${fieldLabel(field, unit)}`}
            onClick={() => selectField(field)}
            className={cn(
              'rounded-pill px-2 py-[3px] text-[9px] font-black uppercase tracking-[0.08em] transition-colors duration-150',
              activeField === field ? 'bg-brand-white/15 text-accent' : 'bg-brand-white/10 text-brand-white/65 hover:text-accent',
            )}
          >
            {FIELD_SHORT[field]}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  /**
   * The ONE transport control, the primary action of the view. It shows ONLY the
   * ▶/⏸ symbol (the set timer lives up in the strip now) and carries the orange
   * outline that marks the primary action. A press plays or pauses; a deliberate
   * ~5 s hold completes the running set (the ring fills while held and releasing
   * early cancels it). Only the large in-panel key holds; the dock key is a
   * plain play/pause.
   */
  const transport = (size: 'large' | 'dock') => {
    const large = size === 'large';
    const isLastSet = safeCursor >= steps.length - 1;
    const holdAction = isLastSet ? 'finish the session' : 'complete the set';
    return (
      <button
        type="button"
        data-testid={phase === 'ready' ? 'runtime-start-set' : 'runtime-pause'}
        data-transport={size}
        data-hold-progress={holdProgress > 0 ? holdProgress.toFixed(2) : undefined}
        data-symbol={transportSymbol}
        data-last-set={large && isLastSet ? 'true' : undefined}
        data-set-clock={large ? (countdownShown !== null ? 'countdown' : 'elapsed') : undefined}
        data-run-in={large && phase === 'resting' ? (runIn ? 'true' : 'false') : undefined}
        data-rest-remaining-s={large && phase === 'resting' ? Math.ceil(restRemainingMs / 1000) : undefined}
        data-rest-target-s={large && phase === 'resting' ? restTargetS : undefined}
        aria-pressed={phase === 'ready' ? undefined : paused}
        aria-label={
          phase === 'working'
            ? paused
              ? `Play — resume the clock. Hold to ${holdAction}.`
              : `Pause — freeze the clock. Hold to ${holdAction}.`
            : phase === 'ready'
              ? 'Start the set — begin the ten second countdown'
              : paused
                ? 'Play — resume the clock'
                : 'Pause — freeze the clock'
        }
        onPointerDown={
          large
            ? (event) => {
                try {
                  event.currentTarget.setPointerCapture(event.pointerId);
                } catch {
                  /* capture is best-effort */
                }
                startHold();
              }
            : undefined
        }
        onPointerUp={large ? cancelHold : undefined}
        onPointerLeave={large ? cancelHold : undefined}
        onPointerCancel={large ? cancelHold : undefined}
        onClick={() => {
          if (holdCompleted.current) {
            holdCompleted.current = false;
            return;
          }
          onTransport();
        }}
        className={cn(
          'l2f-runtime-key l2f-runtime-key-primary relative inline-flex shrink-0 items-center justify-center rounded-full text-brand-white',
          large ? 'h-16 w-16' : 'h-10 w-10',
        )}
      >
        {holdProgress > 0 ? (
          <span
            aria-hidden="true"
            data-testid="runtime-hold-ring"
            className="l2f-hold-ring"
            style={{ '--l2f-hold': String(holdProgress) } as React.CSSProperties}
          />
        ) : null}
        {large ? (
          <span
            aria-hidden="true"
            data-testid="runtime-watermark"
            data-symbol={transportSymbol}
            className="relative select-none font-display text-[30px] font-black leading-none text-brand-white"
          >
            {transportSymbol}
          </span>
        ) : (
          <span aria-hidden="true" className="text-[15px] leading-none">
            {transportSymbol}
          </span>
        )}
      </button>
    );
  };

  const pausedStateRegion = (
    <span data-testid="runtime-paused-state" className="sr-only">
      {paused ? 'paused' : 'running'}
    </span>
  );

  /* The media-player keys: ‹ / › are previous/next set and never end the set; the
     − / + pair is the ONLY stepper and it drives the middle drum field. */
  const prevArrow = (
    <button
      type="button"
      data-testid="runtime-prev"
      aria-label="Previous set"
      onClick={goPrev}
      disabled={safeCursor === 0}
      className="l2f-runtime-arrow inline-flex h-14 w-9 shrink-0 items-center justify-center text-[26px] leading-none text-brand-white/70"
    >
      <span aria-hidden="true">‹</span>
    </button>
  );
  const nextArrow = (
    <button
      type="button"
      data-testid="runtime-next"
      aria-label="Next — start the set now during the countdown, skip the rest, or move to the next set"
      onClick={onNext}
      className="l2f-runtime-arrow inline-flex h-14 w-9 shrink-0 items-center justify-center text-[26px] leading-none text-brand-white/70"
    >
      <span aria-hidden="true">›</span>
    </button>
  );
  const decButton = (
    <button
      type="button"
      data-testid={`runtime-${activeField}-dec`}
      data-step-direction="dec"
      aria-label={stepDescription(activeField, -1)}
      onClick={(event) => stepActive(-1, event.shiftKey)}
      className="l2f-runtime-padkey inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-brand-white"
    >
      <MinusIcon className="text-lg" />
    </button>
  );
  const incButton = (
    <button
      type="button"
      data-testid={`runtime-${activeField}-inc`}
      data-step-direction="inc"
      aria-label={stepDescription(activeField, 1)}
      onClick={(event) => stepActive(1, event.shiftKey)}
      className="l2f-runtime-padkey inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-brand-white"
    >
      <PlusIcon className="text-lg" />
    </button>
  );

  /**
   * THE MEDIA-PLAYER CONTROL ROW — exactly one line, left to right:
   *   ‹   −   ( ▶ / ⏸ )   +   ›
   * evenly spaced and centred, with the play/pause in the middle, larger than
   * the rest and the only orange-outlined shape on the face.
   */
  const transportRow = (
    <div
      data-testid="runtime-transport"
      className="flex w-full items-center justify-center gap-3 sm:gap-5"
    >
      {prevArrow}
      {decButton}
      {transport('large')}
      {incButton}
      {nextArrow}
    </div>
  );

  /**
   * The prescription line (or "last time") now sits LOWER: the drum already
   * shows weight, reps, RPE and rest, so the line is printed below the control
   * row, immediately before the collapsed notes. The group/tempo tags stay up
   * beside the exercise name, because the drum does not duplicate them.
   */
  const prescriptionSummary = prescription ? (
    <p data-testid="runtime-prescription" className="text-center text-[11px] text-brand-white/60">
      {previousForMovement.length ? (
        <>
          <span className="font-bold uppercase tracking-[0.1em]">Last time</span>{' '}
          <span className="l2f-tabular font-mono text-brand-white/80">{previousForMovement.join(' · ')}</span>
        </>
      ) : (
        <>
          <span className="font-bold uppercase tracking-[0.1em]">Prescription</span>{' '}
          <span className="l2f-tabular font-mono text-brand-white/80">
            {formatPrescription(prescriptionLine(prescription), unit)}
          </span>
        </>
      )}
    </p>
  ) : null;

  const prescriptionTags =
    prescription && (prescription.supersetGroup !== null || prescription.tempo) ? (
      <div data-testid="runtime-tags" className="flex flex-wrap items-center justify-center gap-1.5">
        {prescription.supersetGroup !== null ? (
          <span
            data-testid="runtime-group-tag"
            className="inline-flex items-center rounded-full bg-accent px-2 py-[2px] text-[10px] font-black uppercase tracking-[0.06em] text-on-accent"
          >
            {groupTagText(prescription.supersetGroup, prescription.groupType)}
          </span>
        ) : null}
        {partners.length ? (
          <span data-testid="runtime-group-partner" className="text-[11px] text-brand-white/70">
            with {partners.join(' + ')}
          </span>
        ) : null}
        {prescription.tempo ? (
          <span
            data-testid="runtime-tempo-tag"
            className="rounded-sm border border-brand-white/25 px-1.5 py-[2px] font-mono text-[10px] font-bold uppercase text-brand-white/80"
          >
            tempo {prescription.tempo}
          </span>
        ) : null}
      </div>
    ) : null;

  const workingSection = step && prescription ? (
    <section
      data-testid={phase === 'resting' ? 'runtime-resting' : 'runtime-working'}
      className={cn('flex flex-col gap-1.5 px-3 pb-2 pt-1 sm:px-4', phase === 'resting' && runIn && 'bg-warning/10')}
    >
      {/* C — the exercise: name, SET n OF m, then the progress indication. */}
      <div className="flex flex-col items-center gap-0.5 text-center">
        {movement.isWarmup ? (
          <span
            data-testid="runtime-warmup-phase"
            className="inline-flex items-center gap-1.5 rounded-pill bg-warning px-2.5 py-[2px] text-[10px] font-black uppercase tracking-[0.1em] text-brand-charcoal"
          >
            <TimerIcon className="text-[11px]" />
            Warm-up · {warmupIndex} of {warmupMovements.length}
          </span>
        ) : null}
        <span data-testid="runtime-exercise-name" className="max-w-[240px] truncate font-display text-[15px] font-black uppercase tracking-[-0.01em]">
          {step.movement.name}
        </span>
        <span data-testid="runtime-set-label" className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-white/60">
          Set {step.setNumber} of {step.totalSets}
        </span>
        {setProgress}
      </div>

      {/* SUPERSET / DROP SET / tempo stay next to the name — the drum does not
          duplicate them. */}
      {prescriptionTags}

      {phase === 'ready' ? (
        <span data-testid="runtime-ready" className="text-center text-[11px] font-bold uppercase tracking-[0.13em] text-brand-white/60">
          {mode === 'resume' ? 'Resumed — press play for the countdown' : 'Press play for the ten second countdown'}
        </span>
      ) : null}

      {/* D — the vertical three-field drum. */}
      <div className="flex w-full min-w-0 items-stretch justify-center">{drum}</div>

      {/* E — the media-player control row: ‹ − (▶/⏸) + › on one line. */}
      {transportRow}

      {/* No one-off action buttons live here. `›` starts the set during the
          countdown, skips the rest and moves to the next set, and the transport
          is the ONE play/pause (a hold finishes the set). Only the countdown
          keeps its Cancel, which opens the keep-or-discard offer. */}
      {phase === 'countdown' ? (
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button type="button" data-testid="runtime-cancel" onClick={offerDiscard} className={cn(chromeButton, 'h-11')}>
            Cancel
          </button>
        </div>
      ) : null}

      <p
        data-testid="runtime-hold-hint"
        className="flex h-[14px] items-center justify-center text-center text-[10px] text-brand-white/55"
      >
        {phase === 'resting'
          ? 'Press › to start the next set now'
          : phase === 'countdown'
            ? 'Press › to start the set now'
            : phase === 'working'
              ? 'Press and hold the play button to finish the set'
              : 'Press play for the countdown · hold the play button to finish the set'}
      </p>

      {phase === 'resting' ? (
        <p
          data-testid="runtime-rest-state"
          className="text-center text-[11px] font-bold uppercase tracking-[0.12em] text-brand-white/60"
        >
          {runIn
            ? 'Get set — counting you in'
            : paused
              ? 'Rest held — the record keeps counting'
              : `Resting · ${restWord(restS)} · the set you just did`}
        </p>
      ) : null}

      {/* F — the prescription line, now largely redundant beside the drum, sits
          just before the (collapsed) notes. */}
      {prescriptionSummary}

      {movement?.notes ? (
        <div className="border-t border-brand-white/10 pt-2 text-brand-white/80">
          <CollapsedNote id={`runtime-${movement.id}`} note={movement.notes} />
        </div>
      ) : null}

      {error ? (
        <p role="alert" data-testid="runtime-error" className="rounded-[16px] border border-danger/40 bg-danger/10 px-4 py-2 text-[12px] text-danger">
          {error}
        </p>
      ) : null}

      {phase === 'resting' && status ? (
        <p data-testid="runtime-rest-status" className="text-center text-[11px] text-brand-white/70">
          {status}
        </p>
      ) : null}
    </section>
  ) : null;

  /**
   * The last set never finishes silently: this asks, and gives the lifter a
   * moment to add how it went — or to keep training and leave the session open.
   */
  const finishSection = (
    <section data-testid="runtime-finish-prompt" className="flex flex-col gap-3 px-5 py-5">
      <div className="flex flex-col gap-1">
        <h2 className="font-display text-[20px] font-black uppercase tracking-[-0.01em]">Finish the session?</h2>
        <p className="text-[12px] leading-relaxed text-brand-white/70">
          Last set logged. Add how it felt and the session is written to your history — anything still waiting to sync is
          sent first.
        </p>
      </div>
      <PendingSyncNotice testId="runtime-finish-pending" className="text-[11px]" />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="runtime-finish-capture"
          onClick={() => setPhase('capture')}
          className="inline-flex h-12 items-center gap-2 rounded-pill bg-accent px-6 text-[13px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
        >
          <CheckIcon className="text-sm" /> Add how it went
        </button>
        <button type="button" data-testid="runtime-finish-now" onClick={() => void finishSession()} disabled={busy} className={cn(chromeButton, 'h-12')}>
          Finish now
        </button>
        <button
          type="button"
          data-testid="runtime-finish-cancel"
          onClick={() => {
            setPaused(false);
            setPhase('working');
            setAnnouncement('Session still open.');
          }}
          className={cn(chromeButton, 'h-12')}
        >
          Keep training
        </button>
      </div>
      {error ? (
        <p role="alert" data-testid="runtime-error" className="rounded-[16px] border border-danger/40 bg-danger/10 px-4 py-2 text-[12px] text-danger">
          {error}
        </p>
      ) : null}
    </section>
  );

  const captureSection = (
    <section data-testid="runtime-capture" className="flex flex-col gap-4 px-5 py-5">
      <div className="flex flex-col gap-1">
        <h2 className="font-display text-[20px] font-black uppercase tracking-[-0.01em]">Finish — how did it go?</h2>
        <p className="text-[12px] text-brand-white/70">
          {totalSets ? `${pluralize(completedSetTargets.length, 'set')} of ${totalSets} logged` : 'All sets logged'} ·
          three taps and the session is written to your log.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand-white/50">Mood</span>
        <div role="group" aria-label="Session mood, 1 to 5" className="flex flex-wrap items-center gap-1.5">
          {[1, 2, 3, 4, 5].map((score) => (
            <button
              key={score}
              type="button"
              data-testid={`runtime-mood-${score}`}
              aria-pressed={moodScore === score}
              onClick={() => setMoodScore(score)}
              title={MOOD_LABELS[score]}
              className={cn(
                'inline-flex h-10 min-w-[42px] items-center justify-center rounded-[14px] border px-2 text-[12px] font-bold',
                moodScore === score ? 'border-accent bg-accent text-on-accent' : 'border-brand-white/25 text-brand-white/80 hover:border-accent hover:text-accent',
              )}
            >
              {score}
            </button>
          ))}
          {moodScore !== null ? (
            <span className="text-[12px] font-bold uppercase tracking-[0.06em]">{MOOD_LABELS[moodScore]}</span>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-5">
        <label className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand-white/50">Perceived exertion</span>
          <select
            data-testid="runtime-rpe-session"
            value={rpeScore === null ? '' : String(rpeScore)}
            onChange={(event) => setRpeScore(event.target.value === '' ? null : Number.parseFloat(event.target.value))}
            className="l2f-tabular rounded-[12px] border border-brand-white/25 bg-brand-charcoal px-2 py-1.5 font-mono text-[13px] font-bold text-brand-white"
          >
            <option value="">—</option>
            {RPE_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand-white/50">Pain</span>
          <select
            data-testid="runtime-pain"
            value={String(painScore)}
            onChange={(event) => setPainScore(Number.parseInt(event.target.value, 10))}
            className={cn(
              'l2f-tabular rounded-[12px] border bg-brand-charcoal px-2 py-1.5 font-mono text-[13px] font-bold',
              painScore >= 4 ? 'border-danger text-danger' : 'border-brand-white/25 text-brand-white',
            )}
          >
            {Array.from({ length: 11 }, (_, index) => String(index)).map((value) => (
              <option key={value} value={value}>
                {value} / 10
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="text-[11px] text-brand-white/55">
        Pain is written per set against the sets you logged in this panel; mood and exertion are saved on the
        session. Exercise and rest are recorded as real elapsed time, pause or not.
      </p>

      {error ? (
        <p role="alert" data-testid="runtime-error" className="rounded-[16px] border-l-2 border-danger bg-danger/10 px-3 py-2 text-[12px] text-danger">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="runtime-finish"
          onClick={() => void finishSession()}
          disabled={busy}
          className="inline-flex h-12 items-center gap-2 rounded-pill bg-accent px-6 text-[13px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-40"
        >
          <CheckIcon className="text-sm" /> Finish session
        </button>
        <button type="button" data-testid="runtime-capture-back" onClick={onClose} className={cn(chromeButton, 'h-12')}>
          Back to the grid
        </button>
      </div>
    </section>
  );

  const completeSection = (
    <section data-testid="runtime-complete" className="flex flex-col gap-4 px-5 py-5">
      <div className="flex flex-col gap-1">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-success">
          <CheckIcon className="text-sm" /> Session complete
        </span>
        <h2 className="font-display text-[20px] font-black uppercase tracking-[-0.01em]">
          {model.label ?? 'Session'} written to your log
        </h2>
        <p className="text-[12px] text-brand-white/70">It is in your session history under the grid.</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="flex flex-col gap-1 rounded-[16px] border border-brand-white/12 px-3 py-2.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-brand-white/50">Volume</span>
          <span data-testid="runtime-summary-volume" className="l2f-tabular font-mono text-[18px] font-bold">
            {formatVolume(summary?.volumeKg ?? 0, unit)}
          </span>
        </div>
        <div className="flex flex-col gap-1 rounded-[16px] border border-brand-white/12 px-3 py-2.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-brand-white/50">Sets</span>
          <span data-testid="runtime-summary-sets" className="l2f-tabular font-mono text-[18px] font-bold">
            {summary?.setsCompleted ?? completedSetTargets.length}
            {summary?.setsPrescribed ? `/${summary.setsPrescribed}` : ''}
          </span>
        </div>
        <div className="flex flex-col gap-1 rounded-[16px] border border-brand-white/12 px-3 py-2.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-brand-white/50">Reps</span>
          <span data-testid="runtime-summary-reps" className="l2f-tabular font-mono text-[18px] font-bold">
            {summary?.totalReps ?? completedSetTargets.reduce((sum, set) => sum + set.reps, 0)}
          </span>
        </div>
        <div className="flex flex-col gap-1 rounded-[16px] border border-brand-white/12 px-3 py-2.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-brand-white/50">PRs</span>
          <span
            data-testid="runtime-summary-prs"
            className={cn('l2f-tabular font-mono text-[18px] font-bold', (summary?.prCount ?? 0) > 0 ? 'text-accent' : 'text-brand-white')}
          >
            {summary?.prCount ?? 0}
          </span>
        </div>
        <div className="flex flex-col gap-1 rounded-[16px] border border-brand-white/12 px-3 py-2.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-brand-white/50">Duration</span>
          <span data-testid="runtime-summary-duration" className="l2f-tabular font-mono text-[18px] font-bold">
            {formatClock(workoutMs)}
          </span>
        </div>
        <div className="flex flex-col gap-1 rounded-[16px] border border-brand-white/12 px-3 py-2.5">
          <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-brand-white/50">Exercise / rest</span>
          <span data-testid="runtime-summary-rest" className="l2f-tabular font-mono text-[18px] font-bold">
            {formatClock(liveExerciseMs())} / {formatClock(liveRestMs())}
          </span>
        </div>
      </div>

      <button
        type="button"
        data-testid="runtime-complete-close"
        onClick={onClose}
        className="inline-flex h-12 items-center justify-center gap-2 rounded-pill bg-accent px-6 text-[13px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
      >
        Back to the grid
      </button>
    </section>
  );

  const discardDialog = discardPrompt ? (
    <div
      data-testid="runtime-discard-confirm"
      role="alertdialog"
      aria-modal="true"
      aria-label="Discard this session?"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          keepSession();
        }
      }}
      className="absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] bg-brand-charcoal/95 p-5"
    >
      <div className="l2f-runtime-well flex w-full max-w-[330px] flex-col gap-3 px-4 py-4">
        <h2 className="font-display text-[17px] font-black uppercase tracking-[-0.01em]">Discard this session?</h2>
        <p className="text-[12px] leading-relaxed text-brand-white/70">
          {discardPrompt === 'cancel'
            ? 'The session was created the moment you pressed start; nothing has been logged yet.'
            : 'The session is closed and stops counting toward volume, streaks and PRs. Sets already logged stay in your history.'}
        </p>
        {discardMessage ? (
          <p data-testid="runtime-discard-message" role="status" className="rounded-[14px] border border-warning/50 bg-warning/10 px-3 py-2 text-[11px] text-warning">
            {discardMessage}
          </p>
        ) : null}
        <div className="flex flex-col gap-2 sm:flex-row-reverse sm:items-center">
          <button
            ref={keepButtonRef}
            type="button"
            data-testid="runtime-discard-keep"
            onClick={keepSession}
            className="inline-flex h-11 flex-1 items-center justify-center rounded-pill bg-accent px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover"
          >
            Keep session
          </button>
          <button
            type="button"
            data-testid="runtime-discard-yes"
            onClick={() => void confirmDiscard()}
            disabled={discardBusy}
            className="inline-flex h-11 flex-1 items-center justify-center rounded-pill border border-danger/70 px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-danger hover:bg-danger/10 disabled:opacity-40"
          >
            {discardBusy ? 'Discarding…' : 'Discard session'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  /* ------------------------------- the dock ------------------------------ */

  const dockName = step?.movement.name ?? 'Session';
  const dockSet = step?.setNumber ?? 0;
  const dockTotal = step?.totalSets ?? 0;
  /** The dock carries the continuous SESSION timer (always moving) plus the set clock. */
  const dockSessionTimer = formatClock(workoutMs);
  const dockSetTimer =
    phase === 'countdown'
      ? String(countdownValue ?? '')
      : phase === 'working'
        ? formatClock(displayWorkMs())
        : phase === 'resting'
          ? runIn
            ? String(runInValue ?? '')
            : formatClock(restDisplayMs)
          : phase === 'capture'
            ? 'Finish'
            : phase === 'complete'
              ? 'Done'
              : 'Ready';

  if (minimized) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-[95] flex justify-center px-3 pb-3" data-testid="runtime-dock-bar">
        <div className="l2f-runtime-face flex w-full max-w-[430px] items-center gap-2 rounded-[22px] border border-brand-white/15 px-2.5 py-2 text-brand-white shadow-modal">
          <button
            ref={dockRef}
            type="button"
            data-testid="runtime-dock"
            aria-expanded="false"
            aria-label={`Session still running: ${dockName}, set ${dockSet} of ${dockTotal}, ${dockSessionTimer}. Expand the panel.`}
            onClick={expand}
            className="flex min-w-0 flex-1 items-center gap-3 rounded-[16px] px-2 py-1.5 text-left hover:bg-brand-white/5"
          >
            <span className="flex flex-col items-start">
              <span data-testid="runtime-dock-timer" className="l2f-tabular font-mono text-[18px] font-bold leading-none">
                {dockSessionTimer}
              </span>
              <span
                data-testid="runtime-dock-set-timer"
                className="l2f-tabular font-mono text-[10px] font-bold leading-tight text-brand-white/55"
              >
                {dockSetTimer}
              </span>
            </span>
            <span className="flex min-w-0 flex-col">
              <span data-testid="runtime-dock-name" className="truncate font-display text-[12px] font-black uppercase tracking-[-0.01em]">
                {dockName}
              </span>
              <span data-testid="runtime-dock-set" className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand-white/60">
                Set {dockSet} of {dockTotal}
              </span>
            </span>
          </button>
          {phase !== 'capture' && phase !== 'complete' ? transport('dock') : null}
          <button
            type="button"
            data-testid="runtime-expand"
            aria-label="Expand the session panel"
            onClick={expand}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-brand-white/25 text-brand-white/80 hover:border-accent hover:text-accent"
          >
            <span aria-hidden="true" className="text-[13px] leading-none">▲</span>
          </button>
        </div>
        {liveRegions}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-stretch justify-center sm:items-center sm:p-6" data-testid="runtime-panel" data-phase={phase} role="dialog" aria-label="Session runtime">
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="runtime-backdrop"
        onClick={phase === 'ready' || phase === 'countdown' ? offerDiscard : undefined}
        className="absolute inset-0 cursor-default bg-brand-charcoal/75"
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        className="l2f-runtime-face relative flex h-full w-full min-w-0 flex-col overflow-y-auto overscroll-contain bg-brand-charcoal text-brand-white outline-none sm:h-[720px] sm:max-h-[94vh] sm:w-[min(430px,100%)] sm:rounded-[28px] sm:border sm:border-brand-white/12"
      >
        {header}
        {topStrip}
        {stateLine}
        <PendingSyncNotice className="px-4 pt-1" testId="runtime-pending-sync" />

        {/* ONE panel for every working phase: the countdown, the set and the rest
            share the same layout — only the strip's centre cell and the state
            word change. There is no separate countdown screen. */}
        {phase === 'ready' || phase === 'countdown' || phase === 'working' || phase === 'resting'
          ? workingSection
          : null}
        {phase === 'ready' && !step ? emptyState : null}
        {phase === 'finish' ? finishSection : null}
        {phase === 'capture' ? captureSection : null}
        {phase === 'complete' ? completeSection : null}

        {recordedClocks}
        {pausedStateRegion}
        {liveRegions}
        {discardDialog}
      </div>
    </div>
  );
}
